import { createAuth0Client } from "@auth0/auth0-spa-js";
import { jwtDecode } from "jwt-decode";
import { logError, logInfo, logWarn } from "./helpers/logError";
import { clearPendingOrgSwitch, clearPendingOrgSwitchFor, markOrgSwitchHandedToRedirect } from "./helpers/pendingOrgSwitch";
import TransientAuthTokenError, { isTransientAuthTokenError } from "./types/errors/TransientAuthTokenError";
import Auth0RedirectSuppressedError from "./types/errors/Auth0RedirectSuppressedError";
import OrganizationSwitchNotPerformedError from "./types/errors/OrganizationSwitchNotPerformedError";
import OrganizationSwitchSupersededError, { isOrganizationSwitchSupersededError } from "./types/errors/OrganizationSwitchSupersededError";
import { backoffDelayMs } from "@common-ui/utilities/retry";
import { sparkyColdStartDiagnostics } from "./services/sparkyColdStartDiagnostics";
import { clearTrackedIdentity } from "./helpers/trackedIdentity";

// Long-lived sessions: getTokenSilently() refreshes on-demand via the refresh-token
// grant, the focus + online handlers cover backgrounded-tab / network-restored cases,
// AND (LSP-16971) a proactive timer refreshes shortly before expiry so an actively-open
// tab never lets the access token die and fall to the ITP-blocked iframe. All three drive
// the same org-preserving _silentRefresh; terminal failures surface via onAuthError (the
// session-lost banner) instead of a background redirect.
//
// The Auth0 dashboard side (Allow Offline Access, Refresh Token Rotation,
// Inactivity 30d / Absolute 365d, Tenant session lifetimes) must be
// configured to match — see PR #1125 for the values.

// LSP-17342: where a stranded /callback restore is sent instead of being reloaded. The app
// root's router guard is the only place that can safely decide between "session is live, go
// home" and "no session, restart the login redirect" — and unlike /callback its URL carries
// no spent single-use Auth0 credential. Matches the destination the callback guard itself
// uses on success (router/index.js: `window.location.href = restoredPath || "/"`).
const APP_ROOT_PATH = "/";

// LSP-17342: exactly the paths this app's router resolves to the `callback` route, derived by
// enumerating vue-router 5 directly rather than by reasoning about it. router.resolve() gives
// `callback` for "/callback", "/Callback" and "/callback/" (`sensitive` defaults to false, so
// matching is case-INsensitive — round 1 wrongly assumed "/Callback" was an unrelated sibling)
// and `notfound` for "/callback2", "/callbacks", "/callback/x", "/callback.child",
// "/callback;retry", "/callback@tenant" and "/callback%2Fchild". Break-it round 8 caught an
// earlier, wider version of this pattern over-matching that punctuation family and navigating
// away from healthy restores of ordinary not-found pages. A pathname that is not a
// well-formed absolute path never reaches here — _readPathname rejects it, so a URL like
// "/callback?code=..." fails closed as an UNKNOWN route instead of being force-fit here.
// A genuine location.pathname never contains a query/hash separator (those belong to
// location.search / location.hash) nor a control character. Their presence means the value is
// not a trustworthy pathname — see _readPathname. Written as an explicit scan rather than a
// character-class regex because a control-character class trips eslint's no-control-regex.
const hasUnsafePathnameCharacter = (pathname) => {
	for (let index = 0; index < pathname.length; index += 1) {
		const characterCode = pathname.charCodeAt(index);
		const isQueryOrHashSeparator = characterCode === 0x3f || characterCode === 0x23;
		const isControlCharacter = characterCode <= 0x1f || characterCode === 0x7f;
		if (isQueryOrHashSeparator || isControlCharacter) {
			return true;
		}
	}
	return false;
};

const CALLBACK_ROUTE_PATTERN = /^\/callback\/?$/i;

// LSP-16971: the single set of Auth0 error codes meaning the session can't be silently renewed and
// needs an interactive re-login. ALL background terminal paths — proactive refresh, the focus/online
// refreshers, and getAccessToken's no-redirect branch — classify with this set and surface the
// session-lost banner (never a background redirect). Keep it == the union of getAccessToken's
// login-family + invalid-family redirect arrays.
const TERMINAL_REFRESH_ERRORS = new Set([
	"login_required",
	"consent_required",
	"missing_refresh_token",
	"interaction_required",
	"invalid_grant",
	"invalid_token",
	"unauthorized"
]);
const normalizeAuthErrorCode = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");
const hasAuthErrorField = (err) => typeof err?.error === "string" && err.error.trim() !== "";
const findAuthErrorCodeInValue = (value, errorCodes) => {
	const normalized = normalizeAuthErrorCode(value);
	if (!normalized) {
		return "";
	}
	if (errorCodes.has(normalized)) {
		return normalized;
	}
	for (const code of errorCodes) {
		const codePattern = new RegExp(`(^|[^a-z0-9_])${code}([^a-z0-9_]|$)`);
		if (codePattern.test(normalized)) {
			return code;
		}
	}
	return "";
};
const TRANSIENT_TOKEN_ERRORS = new Set(["timeout", "network_error", "server_error", "access_denied"]);
const RETRYABLE_TOKEN_FETCH_ERRORS = new Set(["timeout", "network_error", "server_error"]);
const TOKEN_FETCH_CLASSIFICATION_ERRORS = new Set([...TERMINAL_REFRESH_ERRORS, ...TRANSIENT_TOKEN_ERRORS]);
const BLOCKED_USER_MESSAGE_PATTERNS = [/\buser\s+is\s+blocked\b/, /\buser\b.{0,120}\bsign\s+in\b.{0,120}\bblocked\b/];
const NETWORK_TOKEN_FETCH_MESSAGE_PATTERNS = [
	/\bfailed to fetch\b/,
	/\bload failed\b/,
	/\bnetworkerror when attempting to fetch\b/,
	/\btimeout when executing ['"]fetch['"]/
];
const TOKEN_FETCH_RETRY_DELAYS_MS = [500, 1500];
const isNavigatorOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const tokenRetryDelay = (delayMs) =>
	new Promise((resolve, reject) => {
		let timeoutId = null;
		const cleanup = () => {
			if (timeoutId !== null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
			if (typeof window !== "undefined") {
				window.removeEventListener("offline", handleOffline);
			}
		};
		const handleOffline = () => {
			cleanup();
			reject(new TransientAuthTokenError("Auth0 token fetch deferred while offline"));
		};

		if (typeof window !== "undefined") {
			window.addEventListener("offline", handleOffline, { once: true });
		}
		timeoutId = setTimeout(() => {
			cleanup();
			resolve();
		}, delayMs);
		if (isNavigatorOffline()) {
			handleOffline();
		}
	});
const getAuthErrorCode = (err) => {
	const errorCode = findAuthErrorCodeInValue(err?.error, TOKEN_FETCH_CLASSIFICATION_ERRORS);
	if (errorCode || hasAuthErrorField(err)) {
		return errorCode;
	}
	return (
		findAuthErrorCodeInValue(err?.error_description, TOKEN_FETCH_CLASSIFICATION_ERRORS) ||
		findAuthErrorCodeInValue(err?.message, TOKEN_FETCH_CLASSIFICATION_ERRORS) ||
		findAuthErrorCodeInValue(typeof err === "string" ? err : "", TOKEN_FETCH_CLASSIFICATION_ERRORS)
	);
};
const getTerminalRefreshErrorCode = (err, errorCode = getAuthErrorCode(err)) => {
	return TERMINAL_REFRESH_ERRORS.has(errorCode) ? errorCode : "";
};
const getTokenFetchRequestKey = (options) => {
	if (options === undefined) {
		return "__default__";
	}
	try {
		return JSON.stringify(options);
	} catch {
		return null;
	}
};
const getAuthErrorSearchText = (err) =>
	[err?.error_description, err?.message, typeof err === "string" ? err : ""]
		.filter((value) => typeof value === "string")
		.join(" ")
		.toLowerCase();
const isNetworkShapedTokenFetchTypeError = (err) =>
	err instanceof TypeError && NETWORK_TOKEN_FETCH_MESSAGE_PATTERNS.some((pattern) => pattern.test(getAuthErrorSearchText(err)));
const isBlockedUserMessage = (message) => BLOCKED_USER_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
const isBlockedUserTokenError = (err, errorCode = getAuthErrorCode(err)) => {
	return (errorCode === "access_denied" || errorCode === "unauthorized") && isBlockedUserMessage(getAuthErrorSearchText(err));
};
const getTerminalTokenFetchErrorCode = (err) => {
	const errorCode = getAuthErrorCode(err);
	const terminalRefreshErrorCode = getTerminalRefreshErrorCode(err, errorCode);
	if (terminalRefreshErrorCode) {
		return terminalRefreshErrorCode;
	}
	return isBlockedUserTokenError(err, errorCode) ? errorCode : "";
};
const isTerminalTokenFetchError = (err) => Boolean(getTerminalTokenFetchErrorCode(err));
const isRetryableTokenFetchError = (err) => {
	const errorCode = getAuthErrorCode(err);
	if (errorCode) {
		return RETRYABLE_TOKEN_FETCH_ERRORS.has(errorCode);
	}
	if (hasAuthErrorField(err)) {
		return false;
	}
	if (err instanceof TypeError) {
		return isNetworkShapedTokenFetchTypeError(err);
	}
	return false;
};
const isTransientTokenFetchError = (err) => {
	if (isTransientAuthTokenError(err) || isNavigatorOffline()) {
		return true;
	}
	if (TRANSIENT_TOKEN_ERRORS.has(getAuthErrorCode(err))) {
		return true;
	}
	return isRetryableTokenFetchError(err);
};
// Pages that load this plugin but must NEVER trigger an Auth0 redirect on
// background events (focus, online). /free-trial in particular is the signup
// form for users who do not have an account yet — bouncing them to Auth0
// mid-form would wipe their work. /account-setup is the same shape. Even an
// already-authenticated user on these pages should be left alone: they may
// have opened the page from another tab to refer a colleague.
const NO_BACKGROUND_REFRESH_PATHS = ["/free-trial", "/account-setup"];
const isNoBackgroundRefreshPath = () => NO_BACKGROUND_REFRESH_PATHS.some((p) => window.location.pathname.startsWith(p));
// LSP-16971 proactive-refresh timing.
const PROACTIVE_REFRESH_SKEW_MS = 120_000; // refresh ~2 min before expiry
const PROACTIVE_REFRESH_MIN_DELAY_MS = 10_000; // floor so a near-expired token can't tight-loop
const PROACTIVE_REFRESH_RETRY_MS = 30_000; // transient-failure retry BASE (doubles per attempt, see _scheduleProactiveRetry)
const PROACTIVE_REFRESH_RETRY_MAX_MS = 15 * 60_000; // backoff ceiling
// setTimeout stores its delay as a SIGNED 32-BIT INT (max 2,147,483,647ms ≈ 24.85 days); anything
// larger is clamped to ~1ms and fires IMMEDIATELY. With 30-day access tokens this turned the
// proactive timer into a 3-5 req/s forced-refresh loop per tab and pinned the Auth0 tenant at its
// global rate limit for ~2 days (Jul 11-13 2026 incident, release 3.91.2). This constant's comment
// is the canonical telling of that incident — other sites reference it. Never hand setTimeout a
// delay above this; chunk the wait and re-derive from the token's exp so ANY token lifetime is safe.
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const clearAuth0LocalCache = () => {
	try {
		const storage = window.localStorage;
		if (!storage) {
			return;
		}
		const keys = new Set(Object.keys(storage));
		for (let i = 0; i < storage.length; i += 1) {
			const key = storage.key(i);
			if (key) {
				keys.add(key);
			}
		}
		for (const key of keys) {
			if (key.startsWith("@@auth0") || key.includes("auth0spajs")) {
				storage.removeItem(key);
			}
		}
	} catch {
		// Best effort only; callers continue with redirect/recovery handling.
	}
};

class AuthService {
	constructor() {
		this.blocked = false;
		// LSP-16670: latched the moment a sign-out begins (synchronously, before logout()'s
		// first await) and held until the browser navigates to Auth0's /v2/logout. While set,
		// every token-refresh / login-redirect path below quiets itself so it can't race the
		// logout redirect and silently re-authenticate the still-live SSO session.
		this.loggingOut = false;
		this.client = null;
		this.user = null;
		this.runningCallback = false;
		this.callbackDone = null;
		this.initialized = false;
		this._tokenListeners = new Set();
		this._lastKnownToken = null;
		this._scribeAuthChannel = null;
		// LSP-19027: organization-switch serialization. THREE separate facts, deliberately NOT one
		// flag — auth.js has been bitten repeatedly by a single boolean asked to mean two things:
		//
		//   _orgSwitchTicketSequence  IDENTITY. Monotonic; every ADMITTED switch takes the next
		//                             ticket. Says WHICH switch, never whether one is running.
		//   _orgSwitchOwner           AUTHORITY. The one admitted switch currently entitled to mint
		//                             and emit a token, or null when no switch is in flight. A
		//                             switch that was REQUESTED but refused never becomes owner, so
		//                             this cannot be read as "a switch was requested".
		//   _orgSwitchGrantChain      ORDERING. A promise tail keeping at most one refresh-token
		//                             grant in flight. Separate from authority because it solves a
		//                             different problem: `getTokenSilently({ cacheMode: "off" })`
		//                             writes the Auth0 SDK's OWN token cache, which every later
		//                             getAccessToken() reads through. Gating the emit alone would
		//                             still let a superseded grant land LAST in that cache and hand
		//                             the wrong organization's token to every subsequent API call.
		this._orgSwitchTicketSequence = 0;
		this._orgSwitchOwner = null;
		this._orgSwitchGrantChain = Promise.resolve();
		// LSP-16971: proactive refresh timer + in-flight guard, and terminal-auth listeners
		// (fire { terminal: true } when the session needs a full interactive re-login; lets
		// authSession raise the existing session-lost banner on the no-redirect background path).
		this._proactiveRefreshTimer = null;
		this._proactiveRefreshInFlight = false;
		// Consecutive transient-failure count driving the retry backoff; reset whenever a
		// healthy pre-expiry cycle is armed from a real token.
		this._proactiveRefreshRetryCount = 0;
		// expiresAt of the last token we armed from. _scheduleProactiveRefresh runs on EVERY
		// _emitTokenChanged (i.e. every API request via getAccessToken), so this drives both
		// the arming-log dedup (one Datadog line per token, not per request) and the backoff
		// protection (only a NEW expiry resets the retry counter / clobbers a pending retry).
		this._lastArmedExpiresAt = null;
		// LSP-16971: latched true once a terminal auth error is surfaced (banner up) so proactive
		// refresh stops doomed attempts until an interactive re-login mints a fresh token; cleared
		// on the next non-null token emit (Bugbot).
		this._proactiveRefreshHalted = false;
		this._authErrorListeners = new Set();
		this._tokenFetchInFlightByKey = new Map();
		this._interactiveAuthRecoveryInFlight = null;
		// Auth0 callback transactions are single-use. Keep every caller that observes
		// the same callback route on one SDK exchange so browser/router re-entry cannot
		// consume it twice and start an erroneous recovery redirect.
		this._authenticationInFlight = null;
		// LSP-17342: latched the moment login() hands navigation to Auth0 via
		// loginWithRedirect. If the browser later RESTORES this page from the
		// back-forward cache (back-button spam on the Auth0 sign-in page), the frozen
		// page is stranded on the LoadingPage — no pending navigation, and nothing
		// re-runs the router guard. See startLoginRedirectRecoveryOnRestore.
		this._loginRedirectInFlight = false;
		// Concurrent-attempt bookkeeping: the latch may only clear when EVERY concurrent
		// login() attempt has failed AND none handed navigation to Auth0 — a rejecting
		// attempt must not unmark another attempt that is still pending or already
		// navigating (break-it round 1).
		this._loginRedirectPendingCount = 0;
		this._loginRedirectNavigationStarted = false;
		// Listener-registration guard so a repeated startup call cannot stack pageshow
		// listeners and multiply the recovery reload (break-it round 1).
		this._loginRedirectRecoveryStarted = false;
		// LSP-17342 (Bugbot HIGH, PR #3873): set once this document's Auth0 code/state
		// exchange has actually succeeded. Purely diagnostic — the recovery action for a
		// restored /callback is the same either way (see startLoginRedirectRecoveryOnRestore)
		// — but without it a Datadog log cannot tell "Back after a completed sign-in" from
		// "Back onto a callback that never finished", which is the distinction that made the
		// original reload-based recovery look safe when it was not.
		this._callbackCompleted = false;
		// LSP-17342 (break-it rounds 6 and 8): set once this document has handed a
		// bfcache-restore recovery navigation to the browser, so a SECOND persisted pageshow —
		// including one dispatched re-entrantly from inside reload()/replace() — cannot fire a
		// second, racing recovery navigation. Deliberately permanent for the document's
		// lifetime: the only thing that clears it is navigate() THROWING, which proves nothing
		// was handed off and the page is still stranded. Nothing else may clear it (github-actions
		// review 5360786614, HIGH) — a cleared latch here re-arms the duplicate recovery.
		this._restoreRecoveryNavigated = false;
		// LSP-17342 (break-it round 5 + github-actions review 5360786614, HIGH): the SECOND fact
		// the single latch above used to carry, split out because the two have different
		// lifetimes and conflating them is what produced both a double-navigation race and a
		// permanent sign-in lockout. This one means "a recovery navigation owns this page, so an
		// AUTOMATIC redirect started now would race it" — the concrete harm being a callback
		// exchange that rejects after the recovery replaced the page, whose recovery redirect can
		// land on the Auth0 login screen with a dead SSO cookie and drop a live session. It gates
		// only redirects the user did not ask for; see _redirectToAuth0.
		this._restoreRecoveryOwnsAutomaticRedirects = false;
		// LSP-17342 (break-it round 8): set as soon as this document attempts the Auth0
		// code/state exchange. This is URL-INDEPENDENT evidence that the document IS the
		// callback page, which matters because the route check reads window.location — an
		// object the app does not own. When the pathname cannot be trusted, this flag still
		// proves a consumed credential is in play, so the restore takes the credential-safe
		// recovery instead of returning early and leaving the user stranded.
		this._callbackStarted = false;
	}

	async init() {
		if (this.initialized) {
			return;
		}
		this.initialized = true;
		window.auth = this;
		// LSP-17342 (Bugbot 3806231689): armed BEFORE the client init, not after. install()
		// calls init() without awaiting it, and getClient() is not promise-memoized — a
		// router-guard login() running in this same window starts its own createAuth0Client
		// and can hand navigation to Auth0 before the await below settles. Arming after it
		// would leave that page in the bfcache with no pageshow listener at all, which is the
		// exact strand this whole fix exists to catch. The listener needs no client, and this
		// placement also survives a getClient() path that never settles or throws.
		this.startLoginRedirectRecoveryOnRestore();
		this.client = await this.getClient();
		this.startTokenRefreshOnFocus();
		this.startTokenRefreshOnOnline();
		this.startAuthBroadcastListener();
		this.startLoggingOutResetOnRestore();
		// LSP-16971: arm the proactive timer if a token already exists; new tokens arm it via _emitTokenChanged.
		this._scheduleProactiveRefresh(this._lastKnownToken);
	}

	startLoggingOutResetOnRestore() {
		// LSP-16670: the loggingOut latch intentionally stays set through the sign-out navigation
		// (see logout()). But if the browser restores this page from the back-forward cache
		// (bfcache) — e.g. the user hits Back after signing out — the frozen page keeps
		// loggingOut=true, which would wedge getAccessToken (returns null) and login()/the
		// refreshers (no-op) until a full reload. Clear the latch on a persisted pageshow so a
		// restored page is usable again.
		window.addEventListener("pageshow", (event) => {
			try {
				if (event.persisted && this.loggingOut) {
					logInfo("auth.startLoggingOutResetOnRestore: cleared sign-out latch after bfcache restore", {});
					this.loggingOut = false;
				}
			} catch (error) {
				// break-it round 3 (LSP-17342): same guard as the sibling recovery listener —
				// `persisted` is a property read on a browser/extension-supplied event, and an
				// uncaught throw from an event handler is invisible. Found while hardening the
				// recovery listener; the two share the bfcache-restore path, so leaving one
				// guarded and one bare would mean a malformed event still escapes on restore.
				logError("auth.startLoggingOutResetOnRestore: could not evaluate the restored page", error, {});
			}
		});
	}

	startLoginRedirectRecoveryOnRestore() {
		// LSP-17342: an unauthenticated visit funnels through login() → loginWithRedirect,
		// which navigates to the Auth0 sign-in page while the app is frozen showing the
		// "Loading, please wait..." LoadingPage. Pressing Back on the Auth0 page (spam-clicking
		// makes it certain) can restore that frozen page from the back-forward cache: no
		// navigation is pending anymore and the router guard never re-runs, so the user is
		// stranded on the loading screen indefinitely (observed 10+ min in LSP-17342). The
		// /callback route strands the same way — its guard branch already consumed the
		// single-use code/state and navigated away, so a bfcache restore has nothing running.
		// Recover by re-running the router guard, which either recognizes the session or
		// restarts the login redirect. HOW depends on the route, and the difference is
		// load-bearing: an ordinary page is RELOADED, but a restored /callback is sent to the
		// app root instead, because its URL still carries the single-use code/state the
		// callback already consumed and reloading it re-submits that dead credential (see the
		// branch below — github-actions review 5360786614 #5 flagged this header for implying a
		// reload in both cases, and a misleading comment in this same area already caused one
		// regression). Scoped tightly — only a PERSISTED (bfcache) restore, and only when this
		// page had handed navigation to Auth0 or is the /callback route; normal restores of an
		// authenticated page are untouched.
		if (this._loginRedirectRecoveryStarted) {
			// Idempotence guard: a second registration would stack listeners and fire the
			// recovery reload once per registration (break-it round 1).
			logWarn("auth.startLoginRedirectRecoveryOnRestore: recovery listener already registered; skipping duplicate", {});
			return;
		}
		this._loginRedirectRecoveryStarted = true;
		window.addEventListener("pageshow", (event) => {
			try {
				this._recoverIfStrandedOnRestore(event);
			} catch (error) {
				// break-it round 3: every value this listener inspects — `event.persisted`,
				// `window.location` — is a property read on an object it does not own, and a
				// throwing getter (malformed/synthetic event, extension-replaced location) used
				// to escape as an uncaught error from an event handler. Round 1 claimed this
				// defense but only had it for a non-string pathname, not a throwing one.
				logError("auth.startLoginRedirectRecoveryOnRestore: could not evaluate the restored page; leaving it untouched", error, {});
			}
		});
	}

	// LSP-17342: window.location is not ours — an extension can replace it and a getter can
	// throw or return a non-string. Returns the pathname, or null when it cannot be
	// determined (callers must fail closed on null; see break-it round 4).
	_readPathname() {
		try {
			const rawPathname = window.location?.pathname;
			// Must be a well-formed absolute path to count as KNOWN. A real location.pathname
			// always starts with "/" and never contains "?", "#" or control characters, so
			// anything else is not evidence of ANY route and fails closed to null, letting the
			// caller take the credential-safe recovery. Break-it rounds 7-8: an empty string, a
			// path with no leading slash, a full absolute URL, a glued-on query/hash, and
			// embedded NUL/CR/LF were all previously accepted as "known", which made the route
			// look non-callback and RELOADED it — while href/search can still hold the consumed
			// code/state, so that reload re-submits the credential.
			if (typeof rawPathname !== "string" || !rawPathname.startsWith("/") || hasUnsafePathnameCharacter(rawPathname)) {
				// The fourth unlogged exit in this gate (review 5023486490 #9). Deciding "route
				// unknown" silently is what made break-it rounds 4/7/8 archaeology: the shape of
				// the rejected value is the whole diagnosis. Logged without the value itself,
				// which can carry a consumed code/state.
				logWarn("auth: location.pathname was not a usable absolute path; treating the route as unknown", {
					pathnameType: typeof rawPathname,
					startsWithSlash: typeof rawPathname === "string" ? rawPathname.startsWith("/") : null,
					hasUnsafeCharacter: typeof rawPathname === "string" ? hasUnsafePathnameCharacter(rawPathname) : null,
					length: typeof rawPathname === "string" ? rawPathname.length : null
				});
				return null;
			}
			return rawPathname;
		} catch (error) {
			// The thrown value is not ours either — reading `.message` off it can itself throw
			// (break-it round 8), which would have skipped the recovery entirely.
			let message = null;
			try {
				message = error?.message ?? null;
			} catch {
				message = "unreadable";
			}
			logWarn("auth: could not read location.pathname; treating the route as unknown", { message });
			return null;
		}
	}

	_recoverIfStrandedOnRestore(event) {
		// break-it round 6: a second persisted pageshow in the same document used to fire a
		// second recovery navigation, racing the first. This latch is set ONLY when navigate()
		// did not throw, so a demonstrably FAILED recovery is still retried on the next
		// pageshow (break-it round 2) while a committed one is never duplicated.
		if (this._restoreRecoveryNavigated) {
			// CLAUDE.md "Diagnostic logging when fixing silent failures" (review 5023486490 #9):
			// this PR is itself a fix for a silent failure in this method's own gate, so every
			// fail-condition here has to name itself. Rare by construction — at most once per
			// extra restore of a document that already recovered.
			logWarn("auth.startLoginRedirectRecoveryOnRestore: skipped — this document already handed off a recovery navigation", {
				restoreRecoveryNavigated: true,
				restoreRecoveryOwnsAutomaticRedirects: this._restoreRecoveryOwnsAutomaticRedirects,
				loginRedirectInFlight: this._loginRedirectInFlight,
				callbackStarted: this._callbackStarted
			});
			return;
		}
		// Strict boolean check: `persisted` is a boolean in every browser; anything else is
		// a synthetic/malformed event and must not trigger a reload (break-it round 1).
		if (event.persisted !== true) {
			// Only a NON-persisted pageshow is ordinary here (every normal page load fires one),
			// so log the anomalous shapes and stay quiet on the common one — otherwise this is a
			// line per page load. `persisted` being absent or non-boolean is the synthetic/
			// malformed event break-it round 1 found, and that IS worth a breadcrumb.
			if (typeof event.persisted !== "boolean") {
				logWarn("auth.startLoginRedirectRecoveryOnRestore: skipped — pageshow.persisted was not a boolean", {
					persistedType: typeof event.persisted,
					loginRedirectInFlight: this._loginRedirectInFlight,
					callbackStarted: this._callbackStarted
				});
			}
			return;
		}
		// Defensive: exact route-segment match (not startsWith, which also matched
		// /callback2 and /callbacks) on a defensively-read pathname. Read EXACTLY ONCE
		// (break-it round 3: the previous typeof-then-read double-read let a changing getter
		// report /callback to the guard and /dashboard to the value, bypassing the callback
		// rule). An unreadable pathname is tracked as UNKNOWN rather than coerced to "":
		// break-it round 4 showed that coercing it made the route look non-callback and
		// reloaded it, which on a real /callback?code=... URL re-submits the consumed
		// credential — the session drop this whole branch exists to prevent. Reloading
		// requires positive proof the route is not /callback, so an unknown route fails
		// closed to the credential-safe recovery below.
		const pathname = this._readPathname();
		const routeIsKnown = pathname !== null;
		// Case-INSENSITIVE: verified against vue-router 5 (this app's version) that
		// router.resolve("/Callback") and "/CALLBACK" both resolve to the `callback` route —
		// `sensitive` defaults to false. Break-it round 5 caught round 1's assertion that
		// "/Callback" is not the callback route as factually wrong: a restored
		// "/Callback?code=..." with the latch set would have been RELOADED, re-submitting the
		// consumed credential on a route the router does treat as the callback.
		// The two error directions are NOT symmetric: a false positive costs one navigation to
		// the app root instead of a reload, while a false negative re-submits a consumed Auth0
		// credential and can drop a live session. That asymmetry is handled by _readPathname
		// failing closed, NOT by widening this pattern — CALLBACK_ROUTE_PATTERN is anchored at
		// both ends and matches only "/callback" and "/callback/", exactly the paths the router
		// resolves to `callback`. A query/hash-bearing or control-character pathname never
		// reaches it: _readPathname rejects those as an unknown route, which takes the
		// credential-safe branch below. Do not widen this pattern or add a lookahead to it
		// (github-actions review 5360435887 #2 — an earlier comment here described exactly that
		// and no longer matched the code); widening it navigated away from healthy restores of
		// ordinary not-found pages like /callback.child (break-it round 7).
		// Either signal is enough: the URL says we are on /callback, or this document itself
		// ran the callback exchange (break-it round 8 — the latter survives an unreadable
		// location, which the former does not).
		const onCallbackRoute = (routeIsKnown && CALLBACK_ROUTE_PATTERN.test(pathname)) || this._callbackStarted;
		if (!this._loginRedirectInFlight && !onCallbackRoute) {
			// A healthy restore of an ordinary authenticated page lands here on EVERY bfcache
			// restore, so logging unconditionally would be per-navigation noise for a
			// non-event. Log only the shapes that are actually diagnostic: a pathname we could
			// not read (so "not stranded" rests on the absence of evidence rather than on
			// evidence of absence), or a document that ran a callback exchange yet shows no
			// strand signal. Both are the reconstruct-from-indirect-signals situation CLAUDE.md
			// "Diagnostic logging when fixing silent failures" exists to remove; the plain
			// healthy restore is not (review 5023486490 #9).
			if (!routeIsKnown || this._callbackStarted || this._callbackCompleted) {
				logWarn("auth.startLoginRedirectRecoveryOnRestore: skipped — no strand signal on a restore that is not plainly healthy", {
					routeIsKnown,
					loginRedirectInFlight: this._loginRedirectInFlight,
					onCallbackRoute,
					callbackStarted: this._callbackStarted,
					callbackCompleted: this._callbackCompleted
				});
			}
			return;
		}
		if (onCallbackRoute || !routeIsKnown) {
			// LSP-17342 (Bugbot HIGH 3815975570 + github-actions review 5346906677 #1):
			// NEVER reload a restored /callback. That history entry still carries the
			// single-use code/state this document already consumed, and the router guard
			// runs handleAuthentication() unconditionally — BEFORE its isAuthenticated()
			// branch (router/index.js, the `to.name == "callback"` branch) — so a reload
			// re-submits a dead transaction: handleRedirectCallback throws, and recovery
			// redirects to Auth0 (missing_transaction/invalid_state/state_mismatch -> prompt "login";
			// invalid_grant/invalid_token -> clearAuth0LocalCache + prompt "login"). A
			// normal Back after a SUCCESSFUL sign-in therefore got bounced out of a live
			// session — a worse failure than the stranded loading page being repaired.
			// Leave the dead URL behind and hand the decision to the app root instead: its
			// guard is the correct arbiter for BOTH cases, recognizing a live session (and
			// landing on home) or restarting the login redirect when there genuinely is
			// none. This is why no completed-vs-in-flight branch is needed here — the
			// right action is identical, and reloading was wrong for both. `replace` also
			// drops the consumed-credential entry so a repeat Back cannot land here again.
			this._recoverStrandedRestore(
				() => window.location.replace(APP_ROOT_PATH),
				routeIsKnown
					? "left the restored /callback for the app root instead of re-submitting its consumed transaction"
					: "could not identify the restored route, so recovered via the app root rather than risk reloading a consumed callback",
				{
					callbackCompleted: this._callbackCompleted,
					callbackStarted: this._callbackStarted,
					loginRedirectInFlight: this._loginRedirectInFlight,
					routeIsKnown
				}
			);
			return;
		}
		// Known NOT to be /callback: the URL carries no single-use credential, so reloading
		// the same page is the least surprising recovery — it keeps the user's route, and boot
		// re-runs the router guard, which either recognizes the session or restarts the login
		// redirect.
		this._recoverStrandedRestore(() => window.location.reload(), "reloaded a page stranded mid login redirect", {
			loginRedirectInFlight: this._loginRedirectInFlight,
			pathname
		});
	}

	// LSP-17342 (github-actions review 5346906677 #3): the recovery used to be a single
	// unguarded window.location call inside the pageshow listener. If it throws (location
	// restricted by an extension, frozen document, exotic webview) the exception escapes the
	// listener as an uncaught error carrying none of the state that explains what was being
	// recovered — and the user stays on the loading screen this fix exists to end. Log the
	// attempt, then log the failure with full context so a failed recovery is triagable.
	_recoverStrandedRestore(navigate, description, context) {
		logWarn(`auth.startLoginRedirectRecoveryOnRestore: bfcache-restored a stranded page; ${description}`, context);
		// Both latched BEFORE the call, not after: a synchronous re-entrant pageshow dispatched
		// from inside navigate() would otherwise re-enter with the flags still false and fire a
		// second navigation (break-it round 8). They are set and cleared in exactly these two
		// places and nowhere else, so they can never disagree.
		this._restoreRecoveryNavigated = true;
		this._restoreRecoveryOwnsAutomaticRedirects = true;
		try {
			navigate();
		} catch (error) {
			// navigate() throwing is the one signal that PROVES nothing was handed to the browser:
			// the page is still stranded, so the next restore must be allowed to retry the recovery
			// AND an automatic redirect is no longer racing anything.
			this._restoreRecoveryNavigated = false;
			this._restoreRecoveryOwnsAutomaticRedirects = false;
			logError("auth.startLoginRedirectRecoveryOnRestore: recovery navigation failed; user remains stranded", error, context);
		}
	}

	// LSP-17342 (ls-heroku-ci HIGH, raised on both 64df9180 and a60b1cab): the ONE place that
	// hands top-level navigation to Auth0. Every client.loginWithRedirect() call site funnels
	// through here so the bfcache stranding latch covers all of them. It previously lived
	// inline in login() only, which left setOrganizationId()'s silent-switch fallback — which
	// then freezes the page forever on `await new Promise(() => {})`, and is reachable from
	// the org switcher, a cross-org notification click, AND the router guard on ordinary
	// authenticated boot (router/index.js) — stranding exactly as login() used to, invisible
	// to a latch only login() set. Centralized rather than duplicated per call site, so the
	// next call site added cannot silently miss the latch the way setOrganizationId did.
	//
	// Returns TRUE only when navigation was actually handed to Auth0, FALSE when the redirect
	// was suppressed. Callers that follow this with an unconditional freeze must NOT call it
	// directly — use _redirectToAuth0AndBlockUntilNavigation, which makes "freeze after doing
	// nothing" impossible to write.
	//
	// LSP-17342 (github-actions review 5360786614, HIGH x2): `isExplicitUserIntent` decides
	// whether the bfcache-restore latch may suppress this redirect, and it exists because the
	// page CANNOT reliably tell a committing navigation from a cancelled one. beforeunload fires
	// for every reload()/replace(), not only cancellable ones, and pagehide can fire with
	// `persisted: true` on a page that is coming back — so every signal available to the
	// document is wrong in one direction, and the interaction-based cancellation watch that
	// preceded this was wrong in BOTH (a stray click during a real navigation unlatched it; a
	// bfcache-eligible pagehide after a cancelled one latched it forever). Since the inference
	// cannot be made sound, the suppression is scoped two ways instead — by INTENT and by
	// LIFETIME:
	//
	//   INTENT: only AUTOMATIC redirects — ones the user never asked for — can be suppressed. A
	//   redirect the user just asked for (sign in, switch organization) proceeds regardless,
	//   because a second navigation superseding a stalled first one is ordinary browser
	//   behaviour whereas dropping it is a sign-in lockout on a page the user chose to stay on.
	//   The flag is `isExplicitUserIntent` and it DEFAULTS TO FALSE, i.e. an unclassified call
	//   site is treated as automatic and is therefore suppressible. That direction is deliberate
	//   (review 3857165286): an earlier revision defaulted to "explicit" on the reasoning that a
	//   new call site should fail toward doing what the user asked, but that made the UNSAFE
	//   direction the silent one — a missed classification fired a real redirect into a
	//   committing recovery navigation and could drop a live session on Auth0's login screen,
	//   which is exactly how api/index.ts's token-failure recovery slipped through. Now a missed
	//   classification instead over-suppresses, which costs at most ONE dropped attempt and
	//   self-heals, because the latch is consumed the moment it is spent (see below). The
	//   guard test in tests/unit/authRedirectClassificationGuard.spec.ts additionally fails CI on
	//   any unclassified call site, so the default is a backstop rather than the mechanism.
	//   The full classification lives on _runInteractiveAuthRecovery.
	//
	//   LIFETIME: the latch is CONSUMED by the first automatic redirect it suppresses (review
	//   5023486490 #2). It cannot be document-lifetime, because nothing clears it on a CANCELLED
	//   recovery navigation — only a synchronously-thrown one — so a document-lifetime latch
	//   silently kills every automatic session/token recovery on that page forever, which the
	//   user experiences as things quietly not working. Consuming it needs no inference about
	//   whether the navigation committed: a recovery navigation owns the NEXT navigation of this
	//   document, which is exactly one. If the recovery is genuinely committing the document is
	//   destroyed inside that window and no second automatic redirect arrives, so consuming
	//   costs nothing; if it was cancelled, automatic recovery resumes after one dropped attempt
	//   instead of never. Automatic paths all retry (getAccessToken runs per API call, the
	//   focus/online refreshers re-run, the router replays a deferred boot), so one dropped
	//   attempt is bounded and self-healing where a permanent drop is not.
	async _redirectToAuth0(client, options, callerContext, { isExplicitUserIntent = false } = {}) {
		// break-it round 5: a bfcache-restore recovery owns this page's navigation, so an
		// automatic redirect starting now would race it — most sharply for a callback exchange
		// that rejects AFTER the restore recovery replaced the page, whose recovery redirect can
		// end on the Auth0 login screen and drop a live session. Mirrors the loggingOut latch:
		// the committed navigation owns the page.
		if (!isExplicitUserIntent && this._restoreRecoveryOwnsAutomaticRedirects) {
			// CONSUME the latch as we spend it. The recovery navigation owned the next navigation
			// of this document; that ownership is now discharged, so a later automatic recovery is
			// not silently dead for the document's lifetime (review 5023486490 #2). Cleared BEFORE
			// the return so every exit from this branch clears it — there is only one.
			this._restoreRecoveryOwnsAutomaticRedirects = false;
			logInfo("auth: suppressed an automatic Auth0 redirect — a bfcache-restore recovery owned the navigation; latch now spent", {
				callerContext,
				isExplicitUserIntent,
				restoreRecoveryNavigated: this._restoreRecoveryNavigated
			});
			return false;
		}
		if (this._restoreRecoveryOwnsAutomaticRedirects) {
			// The latch IS set and we are proceeding anyway. Log it: this is the branch that
			// closes the lockout, and it is also the branch that would reintroduce the round-5
			// race if a future automatic call site forgot to pass isExplicitUserIntent. Without
			// this line a wrongly-classified call site is invisible (CLAUDE.md "Diagnostic
			// logging when fixing silent failures").
			logWarn("auth: allowing a user-initiated Auth0 redirect while a bfcache-restore recovery is navigating", {
				callerContext,
				isExplicitUserIntent,
				restoreRecoveryNavigated: this._restoreRecoveryNavigated
			});
		}
		this._loginRedirectPendingCount += 1;
		this._loginRedirectInFlight = true;
		try {
			await client.loginWithRedirect(options);
			// Navigation is handed to Auth0 — the latch must stay set permanently for this
			// page's lifetime so a bfcache restore is recognized as stranded.
			this._loginRedirectNavigationStarted = true;
			// One breadcrumb per sign-in redirect (not per navigation), so a page that
			// resolved loginWithRedirect but never actually left is distinguishable in
			// Datadog from a healthy handoff (github-actions review 5346906677 #2).
			logInfo("auth: handed navigation to Auth0", { callerContext });
			return true;
		} finally {
			this._loginRedirectPendingCount -= 1;
			// Release the latch only when NO attempt is still pending and none navigated:
			// a rejecting attempt must not unmark a concurrent attempt that is still in
			// flight or already handed navigation to Auth0 (break-it round 1).
			if (this._loginRedirectPendingCount === 0 && !this._loginRedirectNavigationStarted) {
				this._loginRedirectInFlight = false;
			}
		}
	}

	// LSP-17342 (github-actions review 5360435887 #1): for callers whose next act is to freeze
	// the page forever because navigation is supposedly underway. The freeze lives HERE, in the
	// same place that checks the handoff, so a caller cannot freeze on a redirect that never
	// happened — the previous shape (funnel returns quietly, caller freezes unconditionally)
	// discarded the user's org switch AND hung the promise it was awaiting, with no throw and
	// no feedback. That combination was introduced by consolidating the redirect call sites
	// into the shared funnel: before it, setOrganizationId called client.loginWithRedirect
	// directly and so either navigated or threw. Throwing is strictly better than hanging:
	// useOrgSwitch turns it into `false`, which SwitchOrganizationDialog surfaces as a toast
	// and which releases its spinner.
	async _redirectToAuth0AndBlockUntilNavigation(client, options, callerContext, redirectOptions) {
		const handedOffToAuth0 = await this._redirectToAuth0(client, options, callerContext, redirectOptions);
		if (!handedOffToAuth0) {
			// A TYPED error, not a bare one: catch sites have to tell this apart from a genuine
			// loginWithRedirect failure, because suppression means "the page is already leaving,
			// do nothing" while a genuine failure means the caller's work did not happen
			// (github-actions review 5360786614, CRITICAL).
			throw new Auth0RedirectSuppressedError(callerContext);
		}
		// loginWithRedirect has called window.location.assign(authUrl). Block here so callers
		// can't race us with their own window.location override before the browser completes
		// the navigation — otherwise the user lands on the post-switch route still holding the
		// old-org token and the next API call 401s.
		await new Promise(() => {});
	}

	onTokenChanged(callback) {
		if (typeof callback !== "function") {
			logError("auth.onTokenChanged: callback was not a function", new Error("non-function callback"));
			return () => {};
		}
		this._tokenListeners.add(callback);
		if (this._lastKnownToken) {
			try {
				callback(this._lastKnownToken);
			} catch (err) {
				logError("auth.onTokenChanged: listener threw on hydrate", err);
			}
		}
		return () => {
			this._tokenListeners.delete(callback);
		};
	}

	onAuthError(callback) {
		if (typeof callback !== "function") {
			logError("auth.onAuthError: callback was not a function", new Error("non-function callback"));
			return () => {};
		}
		this._authErrorListeners.add(callback);
		return () => {
			this._authErrorListeners.delete(callback);
		};
	}

	_emitAuthError(payload) {
		// LSP-16971: a terminal auth failure means the session is lost and the banner owns
		// recovery — halt + disarm the proactive refresh so it stops doomed refresh attempts and
		// 30s retries until a re-login re-arms it (Bugbot). Covers both emit sources
		// (getAccessToken no-redirect path and _runProactiveRefresh).
		if (payload?.terminal) {
			this._proactiveRefreshHalted = true;
			if (this._proactiveRefreshTimer) {
				clearTimeout(this._proactiveRefreshTimer);
				this._proactiveRefreshTimer = null;
			}
		}
		for (const listener of this._authErrorListeners) {
			try {
				listener(payload);
			} catch (err) {
				logError("auth: auth-error listener threw", err);
			}
		}
	}

	// LSP-16971: read exp (ms) from a JWT for proactive scheduling. Mirrors
	// _organizationIdFromToken's defensive decode.
	_expiresAtFromToken(token) {
		if (!token || typeof token !== "string") {
			return null;
		}
		try {
			const claims = jwtDecode(token);
			return claims?.exp ? claims.exp * 1000 : null;
		} catch (err) {
			logError("auth._expiresAtFromToken: failed to decode token", err);
			return null;
		}
	}

	// LSP-17092: shared due-ness test for a forced background refresh. Returns true when the
	// token is missing, has no decodable exp claim, is already expired, or expires within the
	// proactive-refresh skew window — i.e. a forced grant is actually warranted. Returns false
	// when the token still has plenty of life left, so callers (currently only the online-event
	// handler) can skip a needless forced Auth0 grant on network flaps.
	_isTokenDueForRefresh(token) {
		const expiresAt = this._expiresAtFromToken(token);
		if (!expiresAt) {
			return true;
		}
		return expiresAt - Date.now() <= PROACTIVE_REFRESH_SKEW_MS;
	}

	_emitTokenChanged(token) {
		// LSP-16670: while sign-out is in flight, never broadcast a NON-null token. A background
		// refresh (focus/online), an in-flight org switch, or any token fetch that resolves
		// mid-logout would otherwise re-arm authSession + scribe with a fresh token right after
		// logout()'s null-emit — re-establishing the session the user is trying to end. This is
		// the choke-point that closes that class for every caller; logout()'s own null-emit
		// (token == null) is still allowed through so claims still clear on sign-out.
		if (this.loggingOut && token != null) {
			return;
		}
		const next = token ?? null;
		// LSP-16971: a valid token means the session works — un-halt + (re)arm proactive refresh
		// even when the token string is UNCHANGED. A cache-first read after a terminal failure can
		// return the still-valid access token; doing this BEFORE the dedup return keeps auth.js
		// recovery consistent with authSession clearing sessionLost. Otherwise proactive refresh
		// could stay permanently halted while the banner clears (Codex).
		if (next != null) {
			this._proactiveRefreshHalted = false;
			this._scheduleProactiveRefresh(next);
		}
		if (next === this._lastKnownToken) {
			return;
		}
		this._lastKnownToken = next;
		for (const listener of this._tokenListeners) {
			try {
				listener(next);
			} catch (err) {
				logError("auth: token listener threw", err);
			}
		}
	}

	// Clears the dedup cache so a subsequent _emitTokenChanged with the same
	// string actually fires listeners. Used by authSession.dispose() to keep
	// the dedup state in sync with the store's reactive state — without this,
	// a dispose+reinitialize cycle (HMR / tests) could silently drop the
	// first re-emit if it happens to match the pre-dispose token.
	resetTokenCache() {
		this._lastKnownToken = null;
	}

	// Safely read the organization_id claim from a raw token string. Returns null for an
	// absent, non-JWT (e.g. the "mock-token" test sentinel), or malformed token rather than
	// throwing, so callers can compare the active org across a rotation without a try/catch.
	_organizationIdFromToken(token) {
		if (!token || token === "mock-token") {
			return null;
		}
		try {
			const organizationId = jwtDecode(token)?.organization_id;
			// Normalize to a non-empty string or null, mirroring authSession's defense-in-depth
			// (it treats empty-string claims as absent and rejects non-string organization_id).
			// Empty/whitespace/non-string claims must not read as a usable org downstream.
			return typeof organizationId === "string" && organizationId.trim() !== "" ? organizationId : null;
		} catch (err) {
			logError("auth._organizationIdFromToken: failed to decode token", err);
			return null;
		}
	}

	async _getTokenSilentlyWithTransientRetry(client, options, context) {
		const requestKey = getTokenFetchRequestKey(options);
		if (requestKey) {
			const inFlight = this._tokenFetchInFlightByKey.get(requestKey);
			if (inFlight) {
				return inFlight;
			}
		}
		const fetchPromise = this._runTokenSilentlyWithTransientRetry(client, options, context);
		if (!requestKey) {
			return fetchPromise;
		}
		this._tokenFetchInFlightByKey.set(requestKey, fetchPromise);
		try {
			return await fetchPromise;
		} finally {
			if (this._tokenFetchInFlightByKey.get(requestKey) === fetchPromise) {
				this._tokenFetchInFlightByKey.delete(requestKey);
			}
		}
	}

	async _runTokenSilentlyWithTransientRetry(client, options, context) {
		const tokenFetchContext = {
			cacheMode: options?.cacheMode === "off" ? "off" : options?.cacheMode === "cache-only" ? "cache_only" : "default",
			attempt: 1
		};
		const traceTokenFetch = sparkyColdStartDiagnostics.claim("token_silent_fetch");
		const tokenFetchTiming = traceTokenFetch ? sparkyColdStartDiagnostics.startPhase("token_silent_fetch", tokenFetchContext) : null;
		for (let attemptIndex = 0; attemptIndex <= TOKEN_FETCH_RETRY_DELAYS_MS.length; attemptIndex += 1) {
			const navigatorOffline = isNavigatorOffline();
			tokenFetchContext.attempt = attemptIndex + 1;
			if (navigatorOffline && options?.cacheMode !== "off") tokenFetchContext.cacheMode = "cache_only";
			if (navigatorOffline) {
				const offlineError = new TransientAuthTokenError("Auth0 token fetch deferred while offline");
				if (options?.cacheMode !== "off") {
					try {
						const cacheOnlyOptions = { ...(options || {}), cacheMode: "cache-only" };
						const cachedToken = await client.getTokenSilently(cacheOnlyOptions);
						if (typeof cachedToken === "string" && cachedToken.trim() !== "") {
							tokenFetchTiming?.finish("success");
							return cachedToken;
						}
					} catch {
						// Cache-only misses are expected during offline flaps; fall through to the
						// existing transient offline breadcrumb and error.
					}
				}
				tokenFetchTiming?.finish("transient_failure");
				logWarn(`${context}: transient token fetch skipped while offline`, {
					classification: "transient",
					retryable: true,
					offline: true
				});
				throw offlineError;
			}
			try {
				const token = options === undefined ? await client.getTokenSilently() : await client.getTokenSilently(options);
				tokenFetchTiming?.finish("success");
				return token;
			} catch (error) {
				if (isTerminalTokenFetchError(error)) {
					tokenFetchTiming?.finish("terminal_failure");
					logInfo(`${context}: terminal token fetch failure; not retrying`, {
						classification: "terminal",
						errorCode: getTerminalTokenFetchErrorCode(error)
					});
					throw error;
				}
				if (!isTransientTokenFetchError(error)) {
					tokenFetchTiming?.finish("transient_failure");
					logInfo(`${context}: non-terminal token fetch failure was not classified transient`, {
						classification: "unknown",
						errorCode: error?.error,
						errorName: error?.name
					});
					throw error;
				}
				if (!isRetryableTokenFetchError(error)) {
					tokenFetchTiming?.finish("transient_failure");
					logInfo(`${context}: transient token fetch failure deferred to caller backoff`, {
						classification: "transient",
						retryable: false,
						errorCode: error?.error,
						errorName: error?.name
					});
					throw error;
				}

				const nextDelayMs = TOKEN_FETCH_RETRY_DELAYS_MS[attemptIndex];
				if (nextDelayMs == null) {
					tokenFetchTiming?.finish("transient_failure");
					logWarn(`${context}: transient token fetch exhausted bounded retries`, {
						classification: "transient",
						retryable: true,
						attempt: attemptIndex + 1,
						totalAttempts: TOKEN_FETCH_RETRY_DELAYS_MS.length + 1,
						errorCode: error?.error,
						errorName: error?.name
					});
					throw new TransientAuthTokenError("Auth0 token fetch failed transiently after bounded retries", { cause: error });
				}

				logWarn(`${context}: transient token fetch failed; retrying`, {
					classification: "transient",
					retryable: true,
					attempt: attemptIndex + 1,
					totalAttempts: TOKEN_FETCH_RETRY_DELAYS_MS.length + 1,
					nextDelayMs,
					errorCode: error?.error,
					errorName: error?.name
				});
				try {
					await tokenRetryDelay(nextDelayMs);
				} catch (retryDelayError) {
					tokenFetchTiming?.finish("transient_failure");
					throw retryDelayError;
				}
			}
		}
		throw new TransientAuthTokenError("Auth0 token fetch failed transiently after bounded retries");
	}

	async _silentRefresh(extraParams) {
		// LSP-16670: _silentRefresh is the shared refresh helper — reached from the focus/online
		// handlers AND called directly from components (App.vue welcome wizard, OrganizationDetails
		// post-save name rotation), which bypass those handlers' guards. It must not fetch a token
		// during sign-out: getTokenSilently would re-populate the cache logout() is clearing. This
		// is the fetch-side choke-point; callers classify null org-mismatch recovery separately
		// from typed transient token-fetch failures.
		if (this.loggingOut) {
			logInfo("auth._silentRefresh: skipped while sign-out is in flight", {});
			return null;
		}
		const client = await this.getClient();
		if (!client) {
			return null;
		}
		const authorizationParams = { ...(extraParams || {}) };
		const hasExplicitOrganizationId = "organization_id" in authorizationParams;
		if (!hasExplicitOrganizationId) {
			// A bare forced refresh (cacheMode "off", no org param) re-runs the post-login Action
			// without an organization_id and mints a token that drops the active org (LSP-16506).
			// Re-apply the active org so the refreshed JWT keeps it. Read it from the last-known
			// token (a synchronous decode), NOT getOrganizationId() — that helper does its own
			// cache-first getTokenSilently(), which on an expired cache would itself trigger the very
			// no-org refresh we are guarding against, before we could inject the org. Skipped when the
			// caller already supplied an org. This bare branch is the normal path for the online and
			// post-rename refreshes (the focus handler reaches _silentRefresh only via its explicit-org
			// drop-recovery, which skips this branch), so it is intentionally not logged — only the
			// mismatch guard below emits.
			const currentOrgId = this._organizationIdFromToken(this._lastKnownToken);
			if (currentOrgId) {
				authorizationParams.organization_id = currentOrgId;
			}
		}
		const options = { cacheMode: "off" };
		if (Object.keys(authorizationParams).length > 0) {
			options.authorizationParams = authorizationParams;
		}
		const requestedOrganizationId = authorizationParams.organization_id || null;
		const token = await this._getTokenSilentlyWithTransientRetry(client, options, "auth._silentRefresh");
		if (requestedOrganizationId) {
			// Use the safe decoder (try/catch + normalization): a null/undefined/malformed forced-refresh
			// token must resolve to null here so the mismatch guard still fires instead of throwing past it.
			const receivedOrganizationId = this._organizationIdFromToken(token);
			if (receivedOrganizationId !== requestedOrganizationId) {
				logError(
					"auth._silentRefresh: returned token organization_id did not match requested organization_id",
					new Error(`Expected organization_id ${requestedOrganizationId}, received ${receivedOrganizationId ?? "none"}`),
					{
						requestedOrganizationId,
						receivedOrganizationId
					}
				);
				clearAuth0LocalCache();
				return null;
			}
		}
		this._emitTokenChanged(token);
		return token;
	}

	startTokenRefreshOnOnline() {
		// Public-path + isAuthenticated guards mirror startTokenRefreshOnFocus.
		// Without the public-path skip, an authenticated user filling out
		// /free-trial in a second tab who reconnects from a network blip
		// could have _silentRefresh fail terminally and get bounced to Auth0
		// mid-form via doDefaultLogin().
		const handler = async () => {
			if (isNoBackgroundRefreshPath()) {
				return;
			}
			// LSP-16670: a reconnect that lands mid sign-out must not refresh a token — that
			// re-populates the cache the logout is clearing and races the logout redirect.
			// Logged because the repo's silent-failure rule requires every event-handler
			// early-exit in a silent-failure fix to leave a breadcrumb.
			if (this.loggingOut) {
				logInfo("auth.startTokenRefreshOnOnline: skipped refresh while sign-out is in flight", {});
				return;
			}
			try {
				const client = await this.getClient();
				if (!client || !(await client.isAuthenticated())) {
					return;
				}
				// LSP-16670: re-check after the awaits — sign-out may have latched while
				// getClient/isAuthenticated were in flight. Don't refresh: it would re-populate
				// the cache logout() is clearing. (The emit choke-point also blocks the re-emit.)
				if (this.loggingOut) {
					return;
				}
				// LSP-17092: on a flappy network, every reconnect used to force a full Auth0 grant
				// even when the token has weeks of life left (21/30-day tokens post-LSP-17085). Skip
				// the forced refresh when the last-known token isn't actually due yet; fall through to
				// the existing forced-refresh behavior for expired/near-expiry/missing tokens.
				if (!this._isTokenDueForRefresh(this._lastKnownToken)) {
					const expiresAt = this._expiresAtFromToken(this._lastKnownToken);
					logInfo("auth.startTokenRefreshOnOnline: token not due; skipping forced refresh", {
						expiresAt,
						msRemaining: expiresAt - Date.now()
					});
					return;
				}
				await this._silentRefresh();
			} catch (err) {
				// LSP-16971: align with the proactive/SSE terminal path — use the full terminal set
				// and surface the session-lost banner instead of a background redirect (a focus/online
				// event must not hijack navigation). Matches _runProactiveRefresh / getAccessToken.
				if (isTerminalTokenFetchError(err)) {
					logError("auth.startTokenRefreshOnOnline: terminal auth failure — surfacing session-lost banner", err, {
						errorCode: getTerminalTokenFetchErrorCode(err)
					});
					this._emitAuthError({ terminal: true });
					return;
				}
				logError("auth.startTokenRefreshOnOnline: transient refresh failed", err);
			}
		};
		window.addEventListener("online", () => {
			void handler();
		});
	}

	startAuthBroadcastListener() {
		if (!window.BroadcastChannel) return;

		this._scribeAuthChannel = new BroadcastChannel("liveswitch-contact-auth");
		this._scribeAuthChannel.addEventListener("message", async (event) => {
			if (event.data?.type !== "scribe-auth-request") return;

			// LSP-16670: during sign-out we must not hand scribe a fresh token. getTokenSilently
			// here would re-populate the cache the logout is clearing, and logout()'s null-emit
			// exists precisely so scribe stops using the token after sign-out — serving one back
			// would undo that. Reply with the same error shape the no-client path uses so the
			// requester doesn't hang waiting for a token.
			if (this.loggingOut) {
				logInfo("auth.startAuthBroadcastListener: refused scribe-auth-request while sign-out is in flight", {});
				this._scribeAuthChannel.postMessage({ type: "scribe-auth-token", error: "Signing out" });
				return;
			}

			try {
				const client = await this.getClient();
				if (!client) {
					this._scribeAuthChannel.postMessage({ type: "scribe-auth-token", error: "No auth client" });
					return;
				}
				// LSP-16670: re-check after the getClient await — sign-out may have latched while it
				// was in flight. Don't fetch/hand scribe a fresh token during teardown.
				if (this.loggingOut) {
					logInfo("auth.startAuthBroadcastListener: sign-out latched mid-request; refusing token", {});
					this._scribeAuthChannel.postMessage({ type: "scribe-auth-token", error: "Signing out" });
					return;
				}
				const token = await client.getTokenSilently();
				this._scribeAuthChannel.postMessage({ type: "scribe-auth-token", token });
			} catch (error) {
				this._scribeAuthChannel.postMessage({ type: "scribe-auth-token", error: error?.message ?? "Failed to get token" });
			}
		});
	}

	// Push-notify scribe (and any other consumer of the liveswitch-contact-auth
	// channel) that the active token's org has changed. Scribe can use this to
	// invalidate its cached token immediately rather than waiting for its next
	// API call to fail. Forward-compatible: if no consumer is listening yet,
	// the message is simply ignored.
	notifyScribeOrgChanged(organizationId) {
		if (!this._scribeAuthChannel) {
			return;
		}
		try {
			this._scribeAuthChannel.postMessage({
				type: "scribe-token-invalidated",
				reason: "org-changed",
				organizationId: organizationId ?? null
			});
		} catch (err) {
			logError("auth.notifyScribeOrgChanged: postMessage failed", err);
		}
	}

	// LSP-16971: (re)arm the proactive refresh from a token's exp. Idempotent — clears any
	// existing timer first, so it's safe to call from both _emitTokenChanged (new token
	// arrived) and _runProactiveRefresh (post-success reschedule) without stacking timers.
	_scheduleProactiveRefresh(token) {
		const expiresAt = this._expiresAtFromToken(token);
		// A re-emit of the SAME token (cache-first read — every API call lands here via
		// _emitTokenChanged) proves nothing about refresh health. While a backed-off retry is
		// pending, keep it: clearing and re-arming at the 10s floor would defeat the backoff
		// and pin every retry log at attempt 1. (The timer check keeps halt-recovery working —
		// _emitAuthError nulls the timer, and re-arming from a same-token read must still work.)
		if (expiresAt && expiresAt === this._lastArmedExpiresAt && this._proactiveRefreshRetryCount > 0 && this._proactiveRefreshTimer) {
			return;
		}
		if (this._proactiveRefreshTimer) {
			clearTimeout(this._proactiveRefreshTimer);
			this._proactiveRefreshTimer = null;
		}
		if (this.loggingOut || this._proactiveRefreshHalted) {
			return;
		}
		if (!expiresAt) {
			// A null/absent token is the NORMAL boot state (init() arms before the first token
			// loads); the real token re-arms via _emitTokenChanged, so stay quiet there. Only log
			// when a present token unexpectedly lacks exp (dev token / malformed JWT) — that case
			// silently disables proactive refresh and is worth tracing.
			if (token) {
				logInfo("auth._scheduleProactiveRefresh: token has no exp; proactive refresh not armed", {});
			}
			return;
		}
		const delay = this._proactiveRefreshDelayFromExpiry(expiresAt);
		// Only a token with a NEW expiry proves a refresh actually succeeded (a cache-first
		// read re-emits the old one — see _emitTokenChanged) — reset the backoff on that, and
		// log the arming once per expiry so a scheduling anomaly is a 30-second Datadog triage
		// without a log line per API request.
		const isNewExpiry = expiresAt !== this._lastArmedExpiresAt;
		if (isNewExpiry) {
			this._proactiveRefreshRetryCount = 0;
		}
		this._lastArmedExpiresAt = expiresAt;
		if (delay > MAX_TIMER_DELAY_MS) {
			// Beyond setTimeout's 32-bit range (see MAX_TIMER_DELAY_MS): an unclamped delay would
			// fire IMMEDIATELY and loop. Sleep the max chunk, then re-derive from the same token;
			// the final chunk lands in range and arms the real pre-expiry refresh.
			if (isNewExpiry) {
				logInfo("auth._scheduleProactiveRefresh: expiry beyond 32-bit timer range; chunking wait", { delayMs: delay, expiresAt });
			}
			this._armProactiveTimer(() => this._scheduleProactiveRefresh(token), MAX_TIMER_DELAY_MS);
			return;
		}
		if (isNewExpiry) {
			logInfo("auth._scheduleProactiveRefresh: armed", { delayMs: delay, expiresAt });
		}
		this._armProactiveTimer(() => void this._runProactiveRefresh(), delay);
	}

	// The ONLY way to arm _proactiveRefreshTimer. The handle nulls itself the moment the timer
	// fires, so a truthy _proactiveRefreshTimer always means "a timer is genuinely pending".
	// Without this, a fired retry timer's stale handle makes the mid-backoff guard above read
	// a DEAD timer as a pending retry and refuse to re-arm — disarming proactive refresh for
	// the token's remaining life (Bugbot). The handle equality check keeps a newer timer armed
	// by a concurrent _emitTokenChanged from being nulled by an older timer's firing.
	_armProactiveTimer(callback, delayMs) {
		const handle = setTimeout(() => {
			if (this._proactiveRefreshTimer === handle) {
				this._proactiveRefreshTimer = null;
			}
			callback();
		}, delayMs);
		this._proactiveRefreshTimer = handle;
	}

	// Single source of truth for "how long until the proactive refresh is due". The arm-time
	// delay and the early-wake storm breaker in _runProactiveRefresh must stay the SAME
	// computation — deriving both from this helper keeps them from drifting.
	_proactiveRefreshDelayFromExpiry(expiresAt) {
		return Math.max(expiresAt - Date.now() - PROACTIVE_REFRESH_SKEW_MS, PROACTIVE_REFRESH_MIN_DELAY_MS);
	}

	// Transient-failure retry with exponential backoff + jitter. A flat cadence retries in
	// tenant-wide lockstep and re-saturates a rate-limited Auth0 (see MAX_TIMER_DELAY_MS /
	// Jul 2026 incident); doubling per attempt (capped) + 50-100% jitter spreads the herd.
	_scheduleProactiveRetry(message, error) {
		// A concurrent _emitTokenChanged during the awaited _silentRefresh can have armed a new
		// timer into _proactiveRefreshTimer; clear it before overwriting or it runs orphaned
		// (untracked and uncancelable — two live refresh loops).
		if (this._proactiveRefreshTimer) {
			clearTimeout(this._proactiveRefreshTimer);
			this._proactiveRefreshTimer = null;
		}
		// LSP-17090: shared jittered-backoff helper — same math as before, now the canonical
		// convention shared with eventsStore's SSE reconnect.
		const retryDelayMs = backoffDelayMs(this._proactiveRefreshRetryCount, PROACTIVE_REFRESH_RETRY_MS, PROACTIVE_REFRESH_RETRY_MAX_MS);
		this._proactiveRefreshRetryCount += 1;
		if (error) {
			logError(message, error, { errorCode: error?.error, retryDelayMs, attempt: this._proactiveRefreshRetryCount });
		} else {
			logInfo(message, { retryDelayMs, attempt: this._proactiveRefreshRetryCount });
		}
		this._armProactiveTimer(() => void this._runProactiveRefresh(), retryDelayMs);
	}

	// LSP-16971: force a refresh BEFORE expiry via the org-preserving _silentRefresh (which
	// uses cacheMode:'off' → the refresh-token grant that Safari ITP does NOT block, and
	// re-injects the active org to avoid the LSP-16506 drop). Reschedules the next cycle
	// DIRECTLY from the returned token (NOT via _emitTokenChanged, whose identical-token
	// dedup could otherwise silently end the loop). Terminal failures raise the banner via
	// onAuthError; transient failures retry.
	async _runProactiveRefresh() {
		if (this.loggingOut) {
			logInfo("auth._runProactiveRefresh: skipped while sign-out is in flight", {});
			return;
		}
		if (this._proactiveRefreshHalted) {
			// Session terminally lost; the banner owns recovery. Don't refresh or reschedule (Bugbot).
			return;
		}
		// LSP-16971: mirror the focus/online refreshers — never disturb signup / account-setup
		// flows (an authenticated visitor there must be left alone; a terminal refresh there would
		// pop the session-lost banner mid-signup). Re-check later in case of SPA navigation (Bugbot).
		if (isNoBackgroundRefreshPath()) {
			logInfo("auth._runProactiveRefresh: skipped on no-background-refresh path; will re-check", {});
			this._armProactiveTimer(() => void this._runProactiveRefresh(), PROACTIVE_REFRESH_RETRY_MS);
			return;
		}
		// Storm breaker: if we woke long before the token is actually due — a timer bug, a clock
		// jump, anything — do NOT burn a forced network grant against Auth0; re-derive the
		// schedule from the token instead. Caps the blast radius of ANY future early-fire cause,
		// not just the 32-bit overflow (see MAX_TIMER_DELAY_MS / Jul 2026 incident).
		const knownExpiresAt = this._expiresAtFromToken(this._lastKnownToken);
		if (knownExpiresAt && this._proactiveRefreshDelayFromExpiry(knownExpiresAt) > PROACTIVE_REFRESH_MIN_DELAY_MS) {
			logInfo("auth._runProactiveRefresh: woke early (token not near expiry); re-arming without refreshing", {
				expiresAt: knownExpiresAt,
				msUntilExpiry: knownExpiresAt - Date.now()
			});
			this._scheduleProactiveRefresh(this._lastKnownToken);
			return;
		}
		if (this._proactiveRefreshInFlight) {
			// Breadcrumb per the silent-failure rule: a double-fire reaching here means two live
			// timers existed — worth seeing in Datadog even though the guard absorbs it.
			logInfo("auth._runProactiveRefresh: skipped; a refresh is already in flight", {});
			return;
		}
		this._proactiveRefreshInFlight = true;
		try {
			const token = await this._silentRefresh();
			if (this.loggingOut) {
				return;
			}
			if (token) {
				this._scheduleProactiveRefresh(token);
			} else {
				// _silentRefresh returned null (e.g. org-mismatch recovery cleared the cache).
				// Treat as transient; retry with backoff.
				this._scheduleProactiveRetry("auth._runProactiveRefresh: refresh returned no token; scheduling retry");
			}
		} catch (error) {
			if (isTerminalTokenFetchError(error)) {
				logError("auth._runProactiveRefresh: terminal auth failure — surfacing session-lost banner", error, {
					errorCode: getTerminalTokenFetchErrorCode(error)
				});
				this._emitAuthError({ terminal: true });
				return; // no reschedule; the banner owns recovery
			}
			// Covers Auth0 rate-limit refusals too (error: "access_denied" / 429): those are
			// transient — the limit clears — but MUST back off, not hammer on a fixed cadence.
			this._scheduleProactiveRetry("auth._runProactiveRefresh: transient refresh failure; scheduling retry", error);
		} finally {
			this._proactiveRefreshInFlight = false;
		}
	}

	startTokenRefreshOnFocus() {
		// Coalesce rapid visibility flips (hidden→visible→hidden→visible): without this, two
		// events in flight each run a refresh — and on the LSP-16506 drop-recovery path that means
		// two forced org-scoped refreshes. The flag is set synchronously before the first await,
		// so a second event fired during an in-flight refresh returns early, and is cleared in
		// `finally` so the next genuine focus still refreshes.
		let refreshInFlight = false;
		document.addEventListener("visibilitychange", async () => {
			if (document.visibilityState !== "visible") {
				return;
			}

			if (isNoBackgroundRefreshPath()) {
				return;
			}

			// LSP-16670: a focus event arriving mid sign-out must not refresh a token —
			// it re-populates the cache the logout is clearing and races the redirect.
			// Logged because the repo's silent-failure rule requires every event-handler
			// early-exit in a silent-failure fix to leave a breadcrumb.
			if (this.loggingOut) {
				logInfo("auth.startTokenRefreshOnFocus: skipped refresh while sign-out is in flight", {});
				return;
			}

			if (refreshInFlight) {
				return;
			}
			refreshInFlight = true;
			try {
				const client = await this.getClient();
				if (!client) {
					return;
				}
				// Only refresh when there's an existing session. Public pages (web forms,
				// project share links, etc.) load this auth plugin too, and we must not
				// force them into Auth0 just because they switched tabs.
				if (!(await client.isAuthenticated())) {
					return;
				}
				// LSP-16670: re-check after the awaits — sign-out may have latched while
				// getClient/isAuthenticated were in flight. Don't fetch a token: it would
				// re-populate the cache logout() is clearing. (The emit choke-point also blocks
				// the re-emit / the org-drop-recovery _silentRefresh below.)
				if (this.loggingOut) {
					return;
				}
				const previousOrganizationId = this._organizationIdFromToken(this._lastKnownToken);
				const token = await client.getTokenSilently();
				// A focus-time refresh can land a freshly rotated JWT; emit so
				// authSession + scribe see the new claims without waiting for
				// the next API call to trigger getAccessToken().
				//
				// LSP-16506 guard: a cache-miss here triggers the SDK's refresh-token grant, which
				// re-runs the post-login Action without an organization_id and can DROP the active
				// org (the claim goes absent). If that happens, re-fetch scoped to the prior org (via
				// the org-preserving _silentRefresh) instead of broadcasting an org-less token. We
				// only recover on a drop (org → absent), NOT on a change to a different valid org —
				// that is a legitimate cross-tab org switch and must be allowed through. Stays
				// cache-first: the org-scoped forced refresh runs only on an actual drop, not on the
				// common cache-hit, so it adds no per-focus network cost.
				const refreshedOrganizationId = this._organizationIdFromToken(token);
				if (previousOrganizationId && !refreshedOrganizationId) {
					// This is the LSP-16506 drop actually firing — log it (logInfo reaches Datadog
					// directly, bypassing the console forward filter) so we get a field signal on how
					// often a focus refresh drops the org and triggers recovery. Never log token strings.
					logInfo(
						"auth.startTokenRefreshOnFocus: focus refresh dropped the active organization; recovering scoped to prior org",
						{
							previousOrganizationId
						}
					);
					await this._silentRefresh({ organization_id: previousOrganizationId });
					return;
				}
				this._emitTokenChanged(token);
			} catch (error) {
				if (isTerminalTokenFetchError(error)) {
					logError("auth.startTokenRefreshOnFocus: terminal auth failure — surfacing session-lost banner", error, {
						errorCode: getTerminalTokenFetchErrorCode(error)
					});
					this._emitAuthError({ terminal: true });
					return;
				}
				logError("auth.startTokenRefreshOnFocus: transient refresh failed", error);
			} finally {
				refreshInFlight = false;
			}
		});
	}

	async isAuthenticated() {
		try {
			const client = await this.getClient();
			if (!client || typeof client.isAuthenticated !== "function") {
				return false;
			}
			return await client.isAuthenticated();
		} catch (error) {
			// Surface to Datadog so corrupted storage / malformed tokens don't get
			// silently masked as "not authenticated" forever.
			logError("auth.isAuthenticated check failed", error);
			return false;
		}
	}

	isBlocked() {
		return this.blocked;
	}

	async waitForCallbackToFinish(timeoutMs = 15000) {
		if (!this.runningCallback || !this.callbackDone) {
			return;
		}
		// Wait until the in-flight callback settles (success OR failure) or the timeout
		// fires. Swallow rejections here so callers (getAccessToken, exchange helpers,
		// eventsStore, scribe api) don't mistake a callback-side error like a blocked
		// user "unauthorized" for a transient token-fetch failure — that would trigger
		// doDefaultLogout and clobber the blocked-user UI.
		await Promise.race([this.callbackDone.catch(() => {}), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
	}

	async getAccessToken(options = {}) {
		const redirectOnAuthFailure = options?.redirectOnAuthFailure !== false;
		const { redirectOnAuthFailure: _appOnlyOption, ...tokenOptions } = options;
		const tokenFetchOptions = Object.keys(tokenOptions).length > 0 ? tokenOptions : undefined;
		// LSP-16670: once sign-out is in flight, the logout redirect owns the navigation.
		// Fetching a token here re-runs getTokenSilently (which can re-populate the cleared
		// cache) and, on a token failure, would call doDefaultLogin/doDefaultLogout — a
		// competing redirect that races and can bounce the user back into the app. Bail with
		// null (every caller already tolerates a null token on transient failure).
		if (this.loggingOut) {
			logInfo("auth.getAccessToken: suppressed while sign-out is in flight", { redirectOnAuthFailure });
			return null;
		}
		try {
			const client = await this.getClient();
			if (!client) {
				// LSP-16670: during sign-out a missing client is expected — bail quietly.
				if (this.loggingOut) {
					return null;
				}
				// LSP-17079: no Auth0 client yet (init race / transient SDK unavailability). Surface a
				// RETRIABLE transient error instead of a bare null — a bare null becomes an
				// UnauthenticatedError that the router treats as "recovery already started" and only
				// defers, leaving the user stuck with no recovery. As a transient, callers defer/retry
				// (the boot online + timed retry re-attempts) and it never forces a logout.
				throw new TransientAuthTokenError("Auth0 client unavailable; token fetch deferred");
			}

			if (this.runningCallback) {
				await this.waitForCallbackToFinish();
			}

			// LSP-16670: re-check after getClient + the (up to 15s) callback wait — sign-out may
			// have latched during those awaits. Skip the fetch so it can't re-populate the cleared
			// cache; the post-fetch re-check below handles a flip during getTokenSilently itself.
			if (this.loggingOut) {
				return null;
			}

			const token = await this._getTokenSilentlyWithTransientRetry(client, tokenFetchOptions, "auth.getAccessToken");
			// LSP-16670: sign-out may have latched while getTokenSilently was in flight. The emit
			// choke-point already blocks the broadcast, but returning null too keeps callers from
			// using a token minted during teardown.
			if (this.loggingOut) {
				return null;
			}
			this._emitTokenChanged(token);
			return token;
		} catch (error) {
			// LSP-16670: once sign-out is in flight, a token failure is expected (logout cleared
			// the cache) and must NOT drive doDefaultLogin/doDefaultLogout from here — a nested
			// logout() could clear the outer sign-out's latch, and a login redirect would race the
			// logout navigation. Bail quietly (no error noise during teardown).
			if (this.loggingOut) {
				return null;
			}
			const terminalErrorCode = getTerminalTokenFetchErrorCode(error);
			const errorCode = terminalErrorCode || getAuthErrorCode(error);
			// Re-auth needed — session is gone, consent is missing, or silent renewal needs
			// interaction. interaction_required in particular means the refresh-token grant can't
			// renew without a full interactive login (e.g. the refresh token was revoked/rotated
			// away while the SSO session is still alive). Treating it as transient left the session
			// wedged — null token → API 401 → re-login loop — never minting a fresh refresh token;
			// a loginWithRedirect re-auths and replaces it (LSP-16670 staging investigation).
			if (["login_required", "consent_required", "missing_refresh_token", "interaction_required"].includes(errorCode)) {
				logInfo("auth.getAccessToken: terminal login-family auth failure", {
					classification: "terminal",
					errorCode,
					redirectOnAuthFailure
				});
				if (redirectOnAuthFailure) {
					await this._runInteractiveAuthRecovery("login");
				} else {
					this._emitAuthError({ terminal: true });
				}
				return null;
			}
			// Terminal token failures — logout so the user can recover with a fresh session.
			if (["invalid_grant", "invalid_token", "unauthorized"].includes(errorCode) || isBlockedUserTokenError(error)) {
				logInfo("auth.getAccessToken: terminal invalid-family auth failure", {
					classification: "terminal",
					errorCode,
					redirectOnAuthFailure
				});
				if (redirectOnAuthFailure) {
					await this._runInteractiveAuthRecovery("logout");
				} else {
					this._emitAuthError({ terminal: true });
				}
				return null;
			}
			if (isTransientAuthTokenError(error) || isTransientTokenFetchError(error)) {
				const transientError = isTransientAuthTokenError(error)
					? error
					: new TransientAuthTokenError("Auth0 token fetch failed transiently", { cause: error });
				logWarn("auth.getAccessToken: transient token retrieval failed; caller may retry", {
					classification: "transient",
					retryable: true,
					errorCode: error?.error,
					errorName: error?.name
				});
				throw transientError;
			}
			logError("auth.getAccessToken: token retrieval failed", error, { errorCode: error?.error });
			// Unknown non-terminal failures still need interactive recovery. Returning null without
			// starting recovery wedges callers that now correctly refuse to send Bearer null.
			if (redirectOnAuthFailure) {
				await this._runInteractiveAuthRecovery("login");
			} else {
				this._emitAuthError({ terminal: true });
			}
			return null;
		}
	}

	// LSP-17342 (reviews 5023486490 #1/#3/#4, 3857165286 HIGH): EVERY path through here is
	// AUTOMATIC by definition — the app reacting to a token failure, with no user click anywhere.
	// That classification has to reach _redirectToAuth0, or the bfcache-restore suppression can
	// never fire for the paths it was written for.
	//
	// THE COMPLETE CLASSIFICATION of everything in apps/contact-admin/src that can reach a
	// top-level Auth0 navigation. Enforced by tests/unit/authRedirectClassificationGuard.spec.ts,
	// which fails CI on any call site that does not state its choice — because five consecutive
	// review rounds on this PR each found one more site where an omission was indistinguishable
	// from a decision, the last being api/index.ts's background-401 recovery.
	//
	//   AUTOMATIC — app-initiated, suppressible ({ isExplicitUserIntent: false })
	//     getAccessToken login-family failure -> _runInteractiveAuthRecovery("login") -> here
	//     getClient login_required                       -> doDefaultLogin
	//     api _handleResponse INVALID_TOKEN / TOKEN_INVALID / UNAUTHORIZED / TOKEN_EXPIRED
	//                                                    -> doDefaultLogin   (review 3857165286)
	//     router guard, Auth0 callback processing failed  -> login
	//     router guard, getMe terminal auth error         -> doDefaultLogin
	//     router guard, token-probe terminal              -> doDefaultLogin
	//     router guard boot org reconcile                 -> setOrganizationId
	//     handleAuthentication exchange failure           -> _redirectToAuth0
	//
	//   EXPLICIT — the user just asked for it ({ isExplicitUserIntent: true })
	//     router guard, /logout route force-login
	//     router guard, unauthenticated route navigation the user drove
	//     router guard, share-link project needing sign-in
	//     App.vue redirectToHome ("Sign In" on the public share view)
	//     App.vue reauth (the session-lost banner button)
	//     ClaimSharePage startLogin / relogin
	//     NoAccount.relogin ("Log In Again")
	//     org switcher and cross-org notification click   -> setOrganizationId
	//
	// The DEFAULT IS AUTOMATIC (isExplicitUserIntent = false) — see _redirectToAuth0 for why that
	// direction is the safe one.
	//
	// "logout" is deliberately NOT suppressible even though it is automatic: silently dropping a
	// sign-out would leave a user believing they are signed out when they are not, and logout()
	// already makes sign-out the authoritative navigation via the `loggingOut` latch, which
	// suppresses competing logins rather than the other way round. It is logged so the decision
	// is visible rather than looking like an unguarded gap.
	async _runInteractiveAuthRecovery(kind) {
		if (this._interactiveAuthRecoveryInFlight) {
			return this._interactiveAuthRecoveryInFlight;
		}
		if (kind === "logout" && this._restoreRecoveryOwnsAutomaticRedirects) {
			logWarn("auth: allowing an automatic sign-out while a bfcache-restore recovery is navigating", {
				kind,
				restoreRecoveryNavigated: this._restoreRecoveryNavigated
			});
		}
		const recoveryPromise = (kind === "logout" ? this.doDefaultLogout() : this.doDefaultLogin({ isExplicitUserIntent: false })).finally(
			() => {
				if (this._interactiveAuthRecoveryInFlight === recoveryPromise) {
					this._interactiveAuthRecoveryInFlight = null;
				}
			}
		);
		this._interactiveAuthRecoveryInFlight = recoveryPromise;
		return recoveryPromise;
	}

	// `isExplicitUserIntent` is forwarded, not defaulted here: doDefaultLogin is reached BOTH
	// automatically (getAccessToken's and getClient's token-failure recovery) and from inside a
	// router navigation the user drove, and only the automatic ones may be suppressed by a
	// bfcache-restore recovery (review 5023486490 #1/#4).
	async doDefaultLogin({ isExplicitUserIntent = false } = {}) {
		// Always return users to the home page after login. Preserving the pre-login URL
		// was flakey across org switches (e.g., a user on a conversation page would land
		// back on it after picking a different org and hit a 404). The sign-out-in-flight
		// guard lives in login() (the shared choke-point), so it covers this path too.
		await this.login({}, false, null, {}, { isExplicitUserIntent });
	}

	async doDefaultLogout() {
		await this.logout({
			logoutParams: {
				returnTo: window.location.protocol + "//" + window.location.host + "/logout"
			}
		});
	}

	async getClient() {
		const traceClientAcquire = sparkyColdStartDiagnostics.claim("auth_client_acquire");
		const clientAcquireTiming = traceClientAcquire ? sparkyColdStartDiagnostics.startPhase("auth_client_acquire") : null;
		if (this.client) {
			clientAcquireTiming?.finish("success");
			return this.client;
		}

		const env = window?.env || {};
		const domain = env.VITE_AUTH_DOMAIN;
		const clientId = env.VITE_AUTH_CLIENTID;
		const audience = env.VITE_AUTH_AUDIENCE;
		const redirectUri = `${window.location.origin}/callback`;

		if (!domain || !clientId) {
			console.error("Auth client configuration is missing. Ensure env is loaded before initializing auth.");
			clientAcquireTiming?.finish("terminal_failure");
			return this.client;
		}

		try {
			this.client = await createAuth0Client({
				domain,
				clientId,
				cacheLocation: "localstorage",
				useRefreshTokens: true,
				// Allow the SDK to fall back to its silent-auth path when the refresh-token
				// grant fails (e.g., third-party cookie edge cases). Keeps users signed in
				// across more browser configurations than refresh tokens alone.
				useRefreshTokensFallback: true,
				authorizationParams: {
					redirect_uri: redirectUri,
					// LSP-17341: the selector Action forwards this stable app-owned entry
					// point; a history restore must start a new SDK transaction here.
					login_uri: `${window.location.origin}/sign-in`,
					audience,
					scope: "openid profile email offline_access"
				}
			});
		} catch (error) {
			clientAcquireTiming?.finish("transient_failure");
			logError("auth.getClient: failed to create Auth0 client", error);
			if (error.error === "login_required") {
				// Automatic: this is client creation failing, not a user asking to sign in
				// (review 5023486490 #4).
				await this.doDefaultLogin({ isExplicitUserIntent: false });
			}
			return null;
		}

		clientAcquireTiming?.finish("success");
		return this.client;
	}

	// LSP-19027: THE ADMISSION RULE — the single place that decides which of several overlapping
	// organization switches is authoritative. It lives here, inside the owner of the switch, rather
	// than at the three call sites (org switcher, cross-org notification click, authenticated-boot
	// reconcile), because coordination spread across callers is precisely how this class of bug
	// recurs: a fourth call site added later inherits the rule for free instead of forgetting it.
	//
	// PRECEDENCE: explicit user intent outranks an automatic reconcile; within the same class, the
	// later switch wins. Justification, against the two alternatives:
	//
	//   "last writer wins" (unconditional) is WRONG. The boot reconcile targets
	//   `me.organization.id`, read from a getMe that may have been issued before the user clicked.
	//   Letting it win would let a stale automatic switch silently drag the user out of the
	//   organization they just chose — a clobbered user action, which is worse than the race it
	//   would be fixing.
	//
	//   "first wins" is WRONG in the other direction. The boot reconcile can be in flight when the
	//   user clicks the switcher, and a refused click is a dead UI control with no explanation.
	//
	// So: an AUTOMATIC switch arriving while an EXPLICIT one owns the decision is refused outright
	// — it never mints, never emits, and never navigates. Every other combination is admitted and
	// supersedes the incumbent, which then loses at its own ownership gate.
	//
	// Note this SUPERSEDES rather than QUEUES. True queueing would be worse: a stale automatic
	// reconcile parked behind the user's explicit switch would eventually reach the front and
	// clobber it, which is the "last writer wins" failure with extra latency. What IS queued is
	// only the refresh-token grant (_awaitOrgSwitchGrantSlot), for Auth0-SDK-cache ordering.
	//
	// Returns the ticket on admission, or null when refused.
	_admitOrgSwitch(organizationId, isExplicitUserIntent) {
		const incumbent = this._orgSwitchOwner;
		if (incumbent && incumbent.isExplicitUserIntent && !isExplicitUserIntent) {
			logWarn("auth.setOrganizationId: refused an automatic org switch while an explicit switch owns the organization", {
				refusedOrganizationId: organizationId,
				owningOrganizationId: incumbent.organizationId,
				owningTicketId: incumbent.ticketId,
				isExplicitUserIntent,
				owningIsExplicitUserIntent: true
			});
			return null;
		}
		this._orgSwitchTicketSequence += 1;
		const ticket = {
			ticketId: this._orgSwitchTicketSequence,
			organizationId,
			isExplicitUserIntent
		};
		this._orgSwitchOwner = ticket;
		if (incumbent) {
			logWarn("auth.setOrganizationId: superseded an in-flight org switch", {
				supersededOrganizationId: incumbent.organizationId,
				supersededTicketId: incumbent.ticketId,
				supersededIsExplicitUserIntent: incumbent.isExplicitUserIntent,
				winningOrganizationId: organizationId,
				winningTicketId: ticket.ticketId,
				isExplicitUserIntent
			});
		}
		return ticket;
	}

	// True only while `ticket` is still THE authoritative switch. Compared by ticketId rather than
	// object identity so a ticket that crossed an await cannot accidentally match a rebuilt record.
	_ownsOrgSwitch(ticket) {
		return this._orgSwitchOwner?.ticketId === ticket.ticketId;
	}

	// Released from the `finally` of every settling exit in setOrganizationId. Guarded by ticketId:
	// a SUPERSEDED switch releasing must NOT null out the winner's ownership, which an unguarded
	// `this._orgSwitchOwner = null` would do — that is the "guard must cover every exit" rule
	// applied in the awkward direction, where the exit must deliberately do nothing.
	_releaseOrgSwitch(ticket) {
		if (this._orgSwitchOwner?.ticketId === ticket.ticketId) {
			this._orgSwitchOwner = null;
		}
	}

	// Serializes the refresh-token grant so at most one is in flight, and returns the release
	// function the caller MUST invoke from a `finally`. The point is the Auth0 SDK's token cache:
	// `cacheMode: "off"` writes it, and the LAST write wins regardless of which switch is
	// authoritative, so without this a superseded grant resolving late would hand organization A's
	// token to every later getAccessToken() even though the emit was correctly withheld.
	//
	// Scoped to the GRANT only — deliberately not around the redirect ladder. The redirect handoff
	// never settles by design (the document is navigating to Auth0), so a chain that included it
	// could be wedged forever by a navigation the browser CANCELS, killing organization switching in
	// a document that survives. That is the LSP-17342 lesson.
	//
	// THE WAIT IS DELIBERATELY UNBOUNDED IN THIS FILE, and that is a correction (Bugbot on PR #4397,
	// "Timeout poisons SDK token cache"). It first carried a 20s cap so a hung grant could not wedge
	// switching for the life of the document. That cap was worse than useless:
	//
	//   1. It did not bound the WORK, only the WAIT. Releasing the slot does not cancel the
	//      predecessor's in-flight `getTokenSilently` — the SDK exposes no cancellation for it — so
	//      the timed-out grant could still land LAST in the SDK cache, hand the superseded
	//      organization's token to the next cache-first getAccessToken(), and reintroduce the exact
	//      wrong-organization defect this serialization exists to fix. A timeout that abandons the
	//      wait but not the work is not a bound; it just makes the race look serialized.
	//   2. It was guarding a state that cannot occur. MEASURED against the pinned SDK
	//      (@auth0/auth0-spa-js 2.19.2): every token HTTP request is bounded by `httpTimeoutMs =
	//      options.httpTimeoutInSeconds ? 1000 * x : 10_000`, enforced with an AbortController and
	//      `setTimeout(() => controller.abort(), timeoutMs)`. getClient() never sets
	//      httpTimeoutInSeconds, so the 10s default applies, and the `useRefreshTokensFallback`
	//      iframe path is itself capped at 60s. getTokenSilently ALWAYS settles — it cannot hang —
	//      so the grant's `finally` always releases this slot.
	//
	// Worse, 20s sat BELOW the SDK's own worst case (10s grant + up to 60s iframe fallback), so the
	// cap was reachable on a merely slow sign-in rather than on a hang. The bound belongs at the
	// layer that owns the request and can actually abort it, which is the SDK.
	async _awaitOrgSwitchGrantSlot() {
		const predecessor = this._orgSwitchGrantChain;
		let releaseSlot = () => {};
		// Publish our own tail BEFORE awaiting, so a switch admitted while we wait queues behind us
		// rather than beside us. The tail only ever resolves (never rejects), so the chain cannot be
		// poisoned by a failing grant.
		this._orgSwitchGrantChain = new Promise((resolve) => {
			releaseSlot = resolve;
		});
		await predecessor;
		return releaseSlot;
	}

	// LSP-17342 (github-actions review 5360786614): `isExplicitUserIntent` is forwarded to the
	// redirect funnel below and says whether the USER asked for this org switch. The org
	// switcher and a cross-org notification click are explicit intent and must never be
	// silently dropped; the router guard's switch on authenticated boot is automatic and must
	// not race a bfcache-restore recovery navigation. Defaults to AUTOMATIC so an unclassified
	// call site fails toward over-suppression, which self-heals, rather than toward racing that
	// navigation (see _redirectToAuth0).
	//
	// EVERY EXIT FROM THIS METHOD, CLASSIFIED (LSP-18866). The caller — the router's
	// authenticated-boot reconcile — decides whether it may continue rendering org-scoped data
	// purely from what this method does, so a resolve that did not switch is indistinguishable from
	// one that did, and that is the wrong-organization defect this PR exists to fix:
	//
	//   exit                                   | classification      | what the caller observes
	//   ---------------------------------------|---------------------|-------------------------------
	//   loggingOut (entry guard)               | legitimately no-op  | resolves "abandoned-signing-out"
	//   loggingOut (after the silent refresh)  | legitimately no-op  | resolves "abandoned-signing-out"
	//   loggingOut (before redirect fallback)  | legitimately no-op  | resolves "abandoned-signing-out"
	//   org id readable AND already the target | already switched    | resolves "already-current"
	//   LSP-19027 admission refused           | SUPERSEDED, no-op    | throws OrganizationSwitch-
	//     (automatic, explicit switch in fl.) |                     |   SupersededError
	//   LSP-19027 loggingOut during the      | legitimately no-op  | resolves "abandoned-signing-out"
	//     grant-slot wait                     |                     |   (nothing minted)
	//   LSP-19027 superseded before the grant | SUPERSEDED, no-op    | throws (nothing minted)
	//   LSP-19027 superseded after the grant  | SUPERSEDED, no emit | throws (token withheld)
	//   LSP-19027 superseded before the       | SUPERSEDED, no      | throws (no navigation)
	//     redirect fallback                   |   navigation        |
	//   org id UNREADABLE                      | not an exit at all  | logs and FALLS THROUGH to the
	//                                          |                     |   recovery ladder below
	//   no Auth0 client                        | FAILED to switch    | throws OrganizationSwitch-
	//                                          |                     |   NotPerformedError
	//   silent refresh-token grant succeeded   | switched            | resolves "switched"
	//   redirect fallback handed off           | switching, page dies| never settles (deliberate
	//                                          |                     |   freeze inside the funnel)
	//   redirect fallback suppressed/failed    | FAILED to switch    | throws (typed or verbatim)
	//
	// Note the UNREADABLE row: it is deliberately NOT an exit. Both short-circuits are wrong there
	// — resolving loses the switch silently, and throwing skips the refresh-grant/redirect ladder
	// that used to re-authenticate the user. A throw is only ever correct once that ladder has been
	// run and failed.
	//
	// The three `loggingOut` exits are safe to treat as benign specifically because sign-out owns
	// the navigation: logout() has already latched `loggingOut` and its redirect is in flight, so
	// the document is leaving and whatever the router renders next is discarded. That is a
	// different situation from a bfcache-restore recovery, which may be CANCELLED and leave the
	// document alive — which is why suppression is NOT treated as benign at the caller.
	//
	// LSP-19027: the four SUPERSEDED rows above are a THIRD classification, distinct from both
	// "switched" and "FAILED to switch". They resolve nothing and they throw, but the throw means "a
	// newer switch owns the organization now", never "the switch broke". The two must not be
	// collapsed: laundering a genuine outage into a supersession would reintroduce LSP-18866 from
	// the other side, so a grant that actually failed always throws its own error verbatim, and
	// OrganizationSwitchSupersededError is thrown ONLY from an explicit ownership check. It does
	// inherit `organizationSwitchNotPerformed`, because the CALLER's correct response is identical
	// in both cases — do not continue, defer and replay — and the router's replay converges: it
	// re-runs getMe on the WINNER's token, so its target becomes the winning organization and the
	// retry resolves "already-current" instead of racing again. See _admitOrgSwitch for the
	// precedence rule and OrganizationSwitchSupersededError for why it carries both markers.
	async setOrganizationId(organizationId, { isExplicitUserIntent = false } = {}) {
		// LSP-16670: an org switch in flight when the user signs out must neither refresh+emit a
		// token (getTokenSilently below repopulates the cache logout() is clearing and broadcasts
		// a fresh token, undoing the null-emit) nor fall back to loginWithRedirect (a competing
		// navigation that can win the logout redirect — and it blocks forever on `new Promise`).
		// Bail before any of it (including the getOrganizationId() preflight, which itself fetches
		// a token) — the page is tearing down, so abandoning the switch is correct.
		if (this.loggingOut) {
			logInfo("auth.setOrganizationId: skipped org switch while sign-out is in flight", {});
			return "abandoned-signing-out";
		}
		// Read through the discriminated reader, not getOrganizationId(), which collapses "the org
		// id is genuinely null" and "I could not read the org id" into one `null` — a
		// one-flag-two-facts conflation exactly like the latch split earlier in this PR. Treating
		// unreadable as "not equal, carry on" is what let the switch resolve without switching.
		const currentOrganization = await this._readOrganizationId();
		if (currentOrganization.readable && currentOrganization.organizationId === organizationId) {
			// LSP-19055 (break-it round 2): the no-op form of a successful switch. The user explicitly
			// asked to be in this organization and already is, so an older pending notice is superseded
			// intent, not failed intent — same reasoning as the "switched" exit below, and EXPLICIT ONLY
			// for the same load-bearing reason: the router's boot reconcile takes this exact exit on
			// every authenticated boot with isExplicitUserIntent: false, and clearing there would delete
			// the notice out from under the boot that is trying to show it.
			if (isExplicitUserIntent) {
				clearPendingOrgSwitch();
			}
			return "already-current";
		}
		if (!currentOrganization.readable) {
			// An UNREADABLE claim must not short-circuit either way. Returning here would resolve
			// without switching (the wrong-organization defect); THROWING here would be just as
			// wrong in the other direction, and briefly was: it skipped the silent refresh-token
			// grant and the loginWithRedirect fallback below, which is the ladder that actually
			// re-authenticates the user. Before this PR an unreadable claim fell through to that
			// ladder and the user recovered. So fall through and ATTEMPT the switch — the throw
			// belongs at the END of the ladder, as the outcome of exhausting it, never as a
			// substitute for running it. Attempting a switch we may not have needed is harmless:
			// the grant below re-mints a token for the target org and verifies the claim came back
			// as asked, so a redundant switch costs one token refresh and nothing else.
			logWarn("auth.setOrganizationId: current organization claim unreadable; attempting the switch rather than assuming", {
				organizationId,
				isExplicitUserIntent
			});
		}
		// LSP-19027: admission happens HERE — after the cheap pre-checks, before anything that
		// mints a token or navigates. Deliberately after the "already-current" short-circuit so the
		// common no-op path still costs one cache-first read and acquires NOTHING: it cannot be
		// refused, cannot be superseded, and never waits behind an unrelated switch in flight.
		const switchTicket = this._admitOrgSwitch(organizationId, isExplicitUserIntent);
		if (!switchTicket) {
			// Refused, not failed: an automatic reconcile arrived while an explicit switch owns the
			// organization. Nothing was minted, emitted or navigated, so there is nothing to
			// release — no ticket was ever taken.
			throw new OrganizationSwitchSupersededError(organizationId, this._orgSwitchOwner?.organizationId ?? null, isExplicitUserIntent);
		}
		try {
			return await this._performAdmittedOrgSwitch(organizationId, isExplicitUserIntent, switchTicket);
		} finally {
			// EVERY settling exit of _performAdmittedOrgSwitch passes through here — the three
			// "abandoned-signing-out" returns, "switched", the no-client throw, both supersession
			// throws, the redirect suppression/failure throw, and any unexpected throw. Guarded by
			// ticketId, so a loser releasing cannot clear the winner's ownership.
			//
			// The ONE exit that does not reach this `finally` is the successful redirect handoff:
			// _redirectToAuth0AndBlockUntilNavigation never settles on success, so this switch holds
			// ownership until the document dies. That is deliberate, not an oversight — the page is
			// navigating to Auth0 for `organizationId`, and any automatic reconcile arriving in that
			// dying window must stay refused rather than mint a token for a different organization
			// against a session that is about to be replaced. An explicit switch in that window is
			// still admitted (the user outranks us), and it changes nothing: the frozen switch has
			// no emit left to make.
			this._releaseOrgSwitch(switchTicket);
		}
	}

	// LSP-19027: the switch proper, reached only once _admitOrgSwitch has granted `switchTicket`
	// authority. Split out from setOrganizationId so the admission + release live in one place
	// around it and cannot be bypassed by an early return added later inside the body.
	async _performAdmittedOrgSwitch(organizationId, isExplicitUserIntent, switchTicket) {
		const client = await this.getClient();
		if (!client) {
			// FAILED to switch, and now genuinely reachable: a client that cannot be created makes
			// _readOrganizationId unreadable above, we fall through, and creation fails again here.
			// This used to be a bare `return`, indistinguishable from success.
			throw new OrganizationSwitchNotPerformedError("no Auth0 client was available", organizationId);
		}

		// Swap orgs silently using a refresh-token grant. Our Auth0 post-login Action
		// reads organization_id from the token request, so no redirect or popup is
		// required when the refresh-token grant succeeds. This preserves SPA state
		// (scroll, unsaved work, open dialogs) and avoids the Auth0 round-trip.
		try {
			// LSP-19027: take the grant slot so this refresh-token grant does not run beside a
			// predecessor's and race it into the Auth0 SDK's token cache.
			const releaseGrantSlot = await this._awaitOrgSwitchGrantSlot();
			try {
				// PRE-MINT GATE. Refuse BEFORE calling getTokenSilently rather than minting and
				// discarding: a cacheMode "off" grant poisons the SDK cache that every later
				// getAccessToken() reads through, and NOTHING downstream can undo that write — the
				// emit gate cannot see it and the SDK offers no way to retract it. So every fact that
				// makes a mint harmful has to be re-read HERE, after the slot wait.
				//
				// THE COMPLETE LIST IS TWO CONDITIONS, and the argument that it is complete is that
				// they are the only mutable state on `this` that (a) can change while we await the
				// slot and (b) makes minting harmful:
				//
				//   loggingOut  sign-out is in flight, so a mint repopulates the very cache logout()
				//               is clearing. Added on Bugbot's "Mint proceeds after logout starts"
				//               (PR #4397): the entry guard and the post-grant re-check bracket this
				//               window but do not cover it, and the post-grant abandon is too late —
				//               it can withhold the emit but the cache write has already happened.
				//   ownership   a newer switch owns the organization, so this mint is for the wrong
				//               one.
				//
				// Nothing else qualifies. Across this entire file the ONLY condition that ever
				// refuses a token FETCH is `loggingOut` — see getAccessToken, _silentRefresh and
				// _emitTokenChanged. `_proactiveRefreshHalted` gates scheduling, not fetching;
				// `blocked` is blocked-user state that no token path consults; and `runningCallback`
				// makes getAccessToken WAIT rather than refuse and has never been consulted by
				// setOrganizationId. So the vocabulary of "do not mint" in this file is exactly
				// {loggingOut}, plus the ownership fact this method introduces.
				//
				// loggingOut is checked FIRST and resolves benignly, matching the three existing
				// sign-out exits: sign-out owns the navigation, so an abandoned switch is correct and
				// is not a supersession. Reporting it as one would conflate "the user is leaving"
				// with "another switch won".
				if (this.loggingOut) {
					logInfo("auth.setOrganizationId: abandoned org switch before minting — sign-out started during the grant-slot wait", {
						organizationId,
						ticketId: switchTicket.ticketId,
						isExplicitUserIntent,
						owningOrganizationId: this._orgSwitchOwner?.organizationId ?? null,
						stage: "before-grant"
					});
					return "abandoned-signing-out";
				}
				if (!this._ownsOrgSwitch(switchTicket)) {
					logWarn("auth.setOrganizationId: abandoned org switch before minting — a newer switch owns the organization", {
						abandonedOrganizationId: organizationId,
						abandonedTicketId: switchTicket.ticketId,
						winningOrganizationId: this._orgSwitchOwner?.organizationId ?? null,
						winningTicketId: this._orgSwitchOwner?.ticketId ?? null,
						isExplicitUserIntent,
						stage: "before-grant"
					});
					throw new OrganizationSwitchSupersededError(
						organizationId,
						this._orgSwitchOwner?.organizationId ?? null,
						isExplicitUserIntent
					);
				}
				const token = await client.getTokenSilently({
					cacheMode: "off",
					authorizationParams: {
						organization_id: organizationId
					}
				});
				// Confirm Auth0's post-login Action actually applied the org switch. A
				// misconfigured Action (or a denied switch) can return 200 with the OLD
				// organization_id baked in — the SDK won't error, so we'd otherwise emit
				// a "switched" token that still resolves to the previous org downstream.
				// Match getOrganizationId() and authSession.organizationId: read only
				// the authoritative `organization_id` claim, not `organization_id_client`.
				// LSP-16670: a sign-out may have begun while this forced refresh was in flight (the
				// entry guard only covers a switch that STARTS after sign-out). Emitting the freshly
				// minted token now would undo logout()'s null-emit and re-populate the cache it is
				// clearing — abandon the switch and let the logout redirect win.
				if (this.loggingOut) {
					logInfo("auth.setOrganizationId: abandoned org switch — sign-out started during the refresh", {});
					return "abandoned-signing-out";
				}
				const decodedToken = jwtDecode(token);
				const tokenOrganizationId = decodedToken?.organization_id ?? null;
				if (tokenOrganizationId !== organizationId) {
					throw new Error(`Expected switched org ${organizationId}, received ${tokenOrganizationId ?? "none"}`);
				}
				// LSP-19027: POST-MINT GATE, and the hard guarantee behind "a superseded switch
				// never emits a token". A newer switch can be admitted while getTokenSilently is in
				// flight; emitting now would broadcast this organization's token to authSession,
				// scribe and every token listener AFTER the app has already committed to the newer
				// organization. The Auth0 SDK cache is separately protected by the grant slot: the
				// winner's grant runs after ours and overwrites what we just wrote to it.
				if (!this._ownsOrgSwitch(switchTicket)) {
					logWarn("auth.setOrganizationId: withheld a minted token — a newer switch owns the organization", {
						abandonedOrganizationId: organizationId,
						abandonedTicketId: switchTicket.ticketId,
						mintedOrganizationId: tokenOrganizationId,
						winningOrganizationId: this._orgSwitchOwner?.organizationId ?? null,
						winningTicketId: this._orgSwitchOwner?.ticketId ?? null,
						isExplicitUserIntent,
						stage: "after-grant"
					});
					throw new OrganizationSwitchSupersededError(
						organizationId,
						this._orgSwitchOwner?.organizationId ?? null,
						isExplicitUserIntent
					);
				}
				console.log("Auth0 organization id set to", organizationId);
				// LSP-19055 (break-it round 1): a switch that actually succeeded makes any older
				// pending-switch notice obsolete. Reachable when an earlier explicit switch is frozen
				// mid-redirect-handoff (it holds a marker and can no longer clear it) and a newer
				// explicit switch is admitted and succeeds silently in this same document. Without this,
				// the next boot adjudicates the newer organization against the older switch's target and
				// warns about a switch the user has already moved on from — a false positive about intent
				// that was superseded, not intent that failed.
				//
				// EXPLICIT ONLY, and this is load-bearing (break-it round 2): only a user-intent switch
				// supersedes an earlier user-intent notice. The router's authenticated-boot reconcile
				// calls setOrganizationId on EVERY boot with isExplicitUserIntent: false — including the
				// very boot that is supposed to adjudicate the pending notice — so letting an automatic
				// switch clear the marker would race App.vue's consumption and delete the notice before
				// the user ever saw it. That is the original silence, reintroduced from inside the fix.
				if (isExplicitUserIntent) {
					clearPendingOrgSwitch();
				}
				this._emitTokenChanged(token);
				return "switched";
			} finally {
				// Hand the grant slot on unconditionally — every exit above, including both
				// supersession throws, the sign-out abandon and the claim-mismatch throw. A slot
				// held past its grant would stall the NEXT switch until the bounded wait expired.
				releaseGrantSlot();
			}
		} catch (error) {
			// LSP-19027: a supersession is not a silent-switch failure and must not be laundered
			// into one. Rethrow it before the logError + redirect ladder below: falling through
			// would fire a full-document loginWithRedirect for an organization the app has already
			// moved off, and would report the benign case with failure diagnostics.
			if (isOrganizationSwitchSupersededError(error)) {
				throw error;
			}
			// Log the full error so we can diagnose silent-switch failures in Datadog
			// before the redirect fallback masks them.
			logError("auth.setOrganizationId: silent org switch failed; falling back to redirect", error, { organizationId });
		}

		// LSP-16670: re-check before the redirect fallback too — a sign-out that began during the
		// silent switch must own the navigation, so don't fire a competing loginWithRedirect here
		// (it would race the logout redirect and then block forever on the `new Promise` below).
		if (this.loggingOut) {
			logInfo("auth.setOrganizationId: skipped redirect fallback — sign-out is in flight", {});
			return "abandoned-signing-out";
		}

		// LSP-19027: the grant genuinely failed AND a newer switch has since taken the organization.
		// Refuse rather than redirect — the redirect fallback navigates the whole document to Auth0
		// for `organizationId`, which is now the wrong organization. The grant's own failure was
		// already logged verbatim above, so the diagnosis survives even though the thrown
		// classification becomes the supersession, which is what the caller needs to act on.
		if (!this._ownsOrgSwitch(switchTicket)) {
			logWarn("auth.setOrganizationId: skipped redirect fallback — a newer switch owns the organization", {
				abandonedOrganizationId: organizationId,
				abandonedTicketId: switchTicket.ticketId,
				winningOrganizationId: this._orgSwitchOwner?.organizationId ?? null,
				winningTicketId: this._orgSwitchOwner?.ticketId ?? null,
				isExplicitUserIntent,
				stage: "before-redirect"
			});
			throw new OrganizationSwitchSupersededError(organizationId, this._orgSwitchOwner?.organizationId ?? null, isExplicitUserIntent);
		}

		// LSP-19055: the redirect below destroys this document, and with it every in-memory channel
		// the callers use to report a failed switch — SwitchOrganizationDialog's toast, App.vue's
		// switchToOrg toast, notifications.openOrgSwitchFailedDialog. Worse, none of them is even
		// reached: _redirectToAuth0AndBlockUntilNavigation never settles on a successful handoff, so
		// the statement after the caller's `await` does not run. Persist the requested organization
		// so the next boot can adjudicate the outcome and tell the user, then clear it again in the
		// catch below if the redirect turns out NOT to have happened (a surviving marker must mean
		// "this document really did navigate away mid-switch").
		//
		// Marked only for an EXPLICIT switch. An automatic reconcile is the app's own bookkeeping —
		// nobody asked for it, so "we couldn't switch organizations" would be a message about an
		// action the user never took. The router's authenticated-boot guard already owns the
		// user-visible outcome of a failed automatic reconcile (its terminal openNoAccountDialog
		// branch), which is a different surface for a different situation.
		// The returned id is this attempt's proof of ownership. Held in a local so the catch below can
		// abandon exactly the notice this attempt seeded and nothing else — the organization alone cannot
		// tell two attempts at the same organization apart (break-it round 7).
		const pendingOrgSwitchId = isExplicitUserIntent ? markOrgSwitchHandedToRedirect(organizationId) : null;

		// Fallback: silent redirect (prompt: "none" means no user interaction unless
		// the session is actually invalid). No popup — users see a brief navigation.
		try {
			// LSP-17342: through the shared latching funnel, NOT client.loginWithRedirect
			// directly — the freeze this performs afterwards is precisely the stranding
			// condition startLoginRedirectRecoveryOnRestore has to recognize. The blocking
			// variant owns that freeze so this method cannot freeze on a redirect that was
			// suppressed and never happened; it throws Auth0RedirectSuppressedError instead, and
			// the catch below rethrows it so each caller can tell suppression (the page is already
			// navigating away — do nothing) from a genuine failure (the switch did NOT happen and
			// the access token still belongs to the old organization).
			await this._redirectToAuth0AndBlockUntilNavigation(
				client,
				{
					appState: {
						organizationId,
						orgSwitch: true
					},
					authorizationParams: {
						organization_id: organizationId,
						prompt: "none"
					}
				},
				"setOrganizationId",
				{ isExplicitUserIntent }
			);
		} catch (redirectError) {
			// LSP-19055: the redirect did NOT happen — either it was suppressed by a bfcache-restore
			// recovery or loginWithRedirect itself rejected. Either way this document survives, so the
			// throw below reaches the caller and its own toast/dialog fires. Drop the marker: leaving
			// it would double-report the same failure on the next reload of this tab, and on the
			// suppressed path it would report a failure the user was never shown in the first place.
			//
			// KEYED to the notice THIS attempt seeded (break-it rounds 5 and 7). A newer explicit switch
			// may already have overwritten the single marker slot and frozen on its own redirect; clearing
			// blind here would delete THAT switch's notice, and it is the one whose document is actually
			// being replaced and has no other channel left. Keying on the organization was not enough
			// either — two attempts can target the same one.
			clearPendingOrgSwitchFor(pendingOrgSwitchId);
			logError("auth.setOrganizationId: redirect fallback failed", redirectError, { organizationId });
			throw redirectError;
		}
	}

	// Public contract unchanged (null on any failure) so its three external callers are untouched,
	// but implemented over the discriminated reader so there is ONE implementation and callers that
	// need the distinction can have it (review 5024093113). Widening the public shape was judged
	// too broad for this PR; the conflation is removed where it caused the defect.
	async getOrganizationId() {
		const result = await this._readOrganizationId();
		return result.readable ? result.organizationId : null;
	}

	// Returns { readable: true, organizationId } or { readable: false }. "Unreadable" and "absent"
	// are different facts: absent is a legitimate answer about the session, unreadable means we know
	// nothing and must not act as though we do.
	async _readOrganizationId() {
		try {
			const client = await this.getClient();
			if (!client) {
				return { readable: false, organizationId: null };
			}
			const token = await client.getTokenSilently();
			const decodedToken = jwtDecode(token);
			// A token that decodes but carries no organization_id IS readable — "this session has
			// no organization" is a real answer, distinct from "the read failed".
			return { readable: true, organizationId: decodedToken?.organization_id ?? null };
		} catch (err) {
			logError("auth._readOrganizationId: failed to read organization_id from token", err);
			return { readable: false, organizationId: null };
		}
	}

	// `isExplicitUserIntent` is a 5th parameter rather than a field on `additionalOptions`, because
	// that object is filtered against validAuth0Params below and would silently drop it (LSP-18866).
	// It defaults to FALSE — i.e. AUTOMATIC, and therefore suppressible. That is the safe direction:
	// an unclassified caller fails toward over-suppression, which costs one dropped attempt and
	// self-heals, rather than toward racing a committing bfcache-restore recovery navigation. Every
	// call site states its choice explicitly, enforced by
	// tests/unit/authRedirectClassificationGuard.spec.ts.
	async login(state = {}, forceLogin = false, _errorCode = null, additionalOptions = {}, { isExplicitUserIntent = false, ignoreQueryParameters = false } = {}) {
		// LSP-16670: login() is the shared choke-point for the redirect-to-Auth0 path —
		// doDefaultLogin, the router auth guards (router/index.js), NoAccount.relogin, and the
		// share-path login all funnel through here. Once sign-out is in flight, any of them
		// firing a loginWithRedirect would win the navigation race against client.logout()'s
		// redirect and, because the SSO session is still live for that instant, silently sign
		// the user back in. Guarding the choke-point (not just the doDefaultLogin wrapper)
		// closes that door for every current and future caller.
		if (this.loggingOut) {
			logInfo("auth.login: suppressed while sign-out is in flight", { forceLogin });
			return;
		}
		const normalizedState = state || {};
		const options = {};

		if (normalizedState.channelId) {
			options.get_register_token = {
				channel_id: normalizedState.channelId,
				device_id: "web"
			};
		}
		if (normalizedState.organizationId) {
			options.organization_id = normalizedState.organizationId;
		}

		// LSP-17341: the restart entry must not inherit a stale account or SSO connection.
		const params = new Proxy(new URLSearchParams(ignoreQueryParameters ? "" : window.location.search), {
			get: (searchParams, prop) => searchParams.get(prop)
		});

		if (params.signup && !forceLogin) {
			options.signup = params.signup;
		}
		if (params.signup_email && !forceLogin) {
			options.signup_email = params.signup_email;
		}
		if (params.connection) {
			options.connection = params.connection;
		}
		if (params.login_hint) {
			options.login_hint = params.login_hint;
		}

		if (forceLogin === true) {
			options.prompt = "login";
		}

		const validAuth0Params = [
			"connection",
			"prompt",
			"login_hint",
			"domain_hint",
			"audience",
			"scope",
			"response_type",
			"response_mode",
			"nonce",
			"error_code",
			"error_description"
		];

		for (const prop in additionalOptions) {
			if (Object.prototype.hasOwnProperty.call(additionalOptions, prop) && validAuth0Params.includes(prop)) {
				options[prop] = additionalOptions[prop];
			}
		}

		const client = await this.getClient();
		if (!client) {
			// LSP-17342 (github-actions review 5346906677 #4): this exit used to be silent, so
			// the deepest strand of all — Auth0 SDK init failed, so login() can never redirect
			// and the user sits on the LoadingPage forever — left no trace at all. It is the
			// one fail-condition on this path that produces no latch and no recovery, which
			// makes it the one that most needs to be visible in Datadog.
			// break-it round 5: read through _readPathname — a throwing location getter must not
			// turn this diagnostic into an exception escaping login().
			logError("auth.login: no Auth0 client; cannot start the login redirect", new Error("auth client unavailable"), {
				pathname: this._readPathname()
			});
			return;
		}

		// LSP-16670: re-check after the getClient await. login() is the redirect choke-point;
		// a sign-out that latched while getClient was in flight means firing loginWithRedirect
		// now would race the logout navigation and (if the SSO cookie survives) silently re-auth.
		if (this.loggingOut) {
			logInfo("auth.login: suppressed after getClient — sign-out is in flight", {});
			return;
		}

		// Reset the blocked flag on a fresh login attempt so a previously-blocked user
		// who has been unblocked in Auth0 can recover without a page reload.
		this.blocked = false;

		// LSP-17342: _redirectToAuth0 latches BEFORE the redirect starts, so a bfcache restore
		// of this exact page (back-button from the Auth0 sign-in screen) is recognized as
		// stranded and recovered — see startLoginRedirectRecoveryOnRestore. It also releases
		// the latch when the redirect never navigated, so a failed attempt cannot mark future
		// restores stranded.
		try {
			await this._redirectToAuth0(
				client,
				{
					appState: normalizedState,
					authorizationParams: options
				},
				"login",
				{ isExplicitUserIntent }
			);
		} catch (error) {
			logError("auth.login: loginWithRedirect failed before navigating to Auth0", error, { errorCode: error?.error });
			throw error;
		}
	}

	async logout(config) {
		// LSP-16670: latch sign-out as the authoritative navigation BEFORE any await, so a
		// concurrent caller running in this same tick already sees it. This is what stops the
		// intermittent "sign-out bounces back to the page" race — see getAccessToken /
		// doDefaultLogin / the focus + online refreshers, which all bail while it is set.
		// Track whether a sign-out was ALREADY in flight: logout() is re-entrant (e.g.
		// getAccessToken's catch -> doDefaultLogout, or a router-guard getMe failure during
		// navigation). A nested call must NOT release the latch on its own failure — only the
		// call that first set it may release it, or the nested failure would reopen the race for
		// the outer sign-out that's still awaiting Auth0.
		const alreadyLoggingOut = this.loggingOut;
		this.loggingOut = true;
		// LSP-19055 (break-it round 1): sign-out abandons any pending organization switch by
		// definition, so drop its notice. The switch's own exits cannot do this for us: the ONE exit
		// that holds a marker is the frozen redirect handoff, which never settles and so never reaches
		// any cleanup. Without this, a sign-out that wins that navigation leaves the marker in the tab
		// and the user's NEXT sign-in is greeted by "we couldn't switch organizations" for a switch
		// they abandoned. Before the first await, alongside the loggingOut latch, so a concurrent
		// caller in this same tick already sees the abandonment.
		clearPendingOrgSwitch();
		if (this._proactiveRefreshTimer) {
			clearTimeout(this._proactiveRefreshTimer);
			this._proactiveRefreshTimer = null;
		}
		// Emit a null-token to listeners before the SDK clears its cache so
		// authSession nulls its claims and scribe (which mirrors the JWT)
		// doesn't keep handing out the stale token after the user signs out.
		this._emitTokenChanged(null);
		try {
			const client = await this.getClient();
			if (!client) {
				// No client means we can't redirect to Auth0. Release the latch (only if WE set it)
				// so the app isn't left permanently refusing tokens, and surface why sign-out
				// stalled (the call site is fire-and-forget, so this is the only place it's visible).
				if (!alreadyLoggingOut) {
					this.loggingOut = false;
				}
				logError("auth.logout: no Auth0 client available; sign-out could not start", new Error("missing auth client"));
				return;
			}
			// Normalize legacy `{ returnTo }` to v2 `{ logoutParams: { returnTo } }`
			// while still accepting pre-formatted v2 configs.
			const normalized = config?.logoutParams ? config : config?.returnTo ? { logoutParams: { returnTo: config.returnTo } } : config;
			await client.logout(normalized);
			// LSP-23357: only after the sign-out went through. A failed sign-out leaves the person signed in, so
			// their tracked identity stays.
			clearTrackedIdentity();
			// Success: client.logout() has called window.location.assign and the page is
			// navigating to Auth0. Intentionally leave loggingOut latched — releasing it now
			// would reopen the race for the brief window before the browser commits.
			// PR #3482: with { openUrl: false } there is NO navigation — the SDK only clears its
			// local session. The latch still stays set, so callers using openUrl:false MUST
			// follow with a full reload (see ClaimSharePage.startLoginDifferentEmail) or the page
			// would be left refusing tokens.
		} catch (error) {
			// A failed sign-out must not brick token retrieval, so release the latch — but only if
			// THIS call set it (a nested logout must not clear the outer sign-out's latch). The
			// call site doesn't await us, so without this log the failure would be silent — which
			// is exactly the silent-failure class this fix addresses. Use optional access on
			// error?.error: a non-object rejection must not throw a TypeError here and mask the
			// original failure (matches the logError payload just below).
			if (!alreadyLoggingOut) {
				this.loggingOut = false;
			}
			if (error?.error !== "login_required") {
				// LSP-16971: a genuine sign-out failure leaves the session alive, but we already emitted
				// null + cleared the proactive timer above. Re-hydrate the token so authSession restores
				// claims and proactive refresh re-arms, instead of leaving the app claims-null + timerless
				// (Codex). Only when THIS call owns the latch (a nested logout must not re-hydrate).
				if (!alreadyLoggingOut) {
					void this.getAccessToken({ redirectOnAuthFailure: false }).catch((rehydrateError) => {
						if (isTerminalTokenFetchError(rehydrateError)) {
							logError("auth.logout: terminal token rehydrate failure after sign-out failure", rehydrateError, {
								errorCode: getTerminalTokenFetchErrorCode(rehydrateError) || rehydrateError?.error
							});
							this._emitAuthError({ terminal: true });
							return;
						}
						if (isTransientAuthTokenError(rehydrateError) || isTransientTokenFetchError(rehydrateError)) {
							logWarn("auth.logout: token rehydrate deferred after sign-out failure", {
								classification: "transient",
								retryable: true,
								errorCode: rehydrateError?.error,
								errorName: rehydrateError?.name
							});
							return;
						}
						logError("auth.logout: token rehydrate failed after sign-out failure", rehydrateError, {
							errorCode: rehydrateError?.error
						});
					});
				}
				logError("auth.logout: sign-out failed before redirect", error, { errorCode: error?.error });
				throw error;
			}
		}
	}

	async handleAuthentication() {
		if (this._authenticationInFlight) {
			return this._authenticationInFlight;
		}

		const authenticationPromise = this._handleAuthentication();
		this._authenticationInFlight = authenticationPromise;
		try {
			return await authenticationPromise;
		} finally {
			if (this._authenticationInFlight === authenticationPromise) {
				this._authenticationInFlight = null;
			}
		}
	}

	async _handleAuthentication() {
		this.runningCallback = true;
		let resolveCallback;
		let rejectCallback;
		this.callbackDone = new Promise((resolve, reject) => {
			resolveCallback = resolve;
			rejectCallback = reject;
		});
		// Avoid an "Unhandled promise rejection" warning if no consumer awaits
		// callbackDone before we reject it on a rethrow path (blocked, origin_mismatch).
		this.callbackDone.catch(() => {});

		const finishWithSuccess = () => {
			this.runningCallback = false;
			if (resolveCallback) {
				resolveCallback();
				resolveCallback = null;
				rejectCallback = null;
			}
		};

		const finishWithError = (error) => {
			this.runningCallback = false;
			if (rejectCallback) {
				rejectCallback(error);
				resolveCallback = null;
				rejectCallback = null;
			}
		};

		const client = await this.getClient();
		if (!client) {
			finishWithSuccess();
			return null;
		}

		// LSP-17342: from here on this document has put its single-use code/state on the wire,
		// so it must never be reloaded (break-it round 8). Set BEFORE the await so a restore
		// during the exchange is covered too.
		this._callbackStarted = true;
		try {
			const result = await client.handleRedirectCallback();
			// LSP-17342: this document's single-use code/state is now spent and the sign-in
			// succeeded. Recorded for the restored-/callback diagnostic log.
			this._callbackCompleted = true;
			finishWithSuccess();
			return result?.appState || null;
		} catch (err) {
			// Extract org-switch context first so the failure log records whether recovery
			// will rehydrate the requested org. Without this attribute, "recovery succeeded"
			// and "recovery silently dropped the org" look identical in Datadog.
			const hasOrgSwitchFlag = err?.appState?.orgSwitch === true;
			const orgSwitchOrganizationId = err?.appState?.organizationId ?? null;
			const orgSwitchAppState = hasOrgSwitchFlag && orgSwitchOrganizationId ? err.appState : null;
			const orgSwitchRecovery = orgSwitchAppState
				? `applied:${orgSwitchOrganizationId}`
				: hasOrgSwitchFlag
					? "missing-organization-id"
					: "none";

			// LSP-17341: malformed SDK error fields must not strand callback waiters.
			const errCode = typeof err?.error === "string" ? err.error.toLowerCase() : "";
			const description = (typeof err?.error_description === "string" && err.error_description) || err?.message;
			const errDesc = typeof description === "string" ? description.toLowerCase() : "";

			if (errCode === "missing_transaction" || errCode === "invalid_state" || errCode === "state_mismatch") {
				// LSP-21984: stale sign-in returns are expected; the recovery redirect has not succeeded yet.
				logWarn("auth.handleAuthentication: stale sign-in return; restarting sign-in", {
					errCode,
					errDesc,
					orgSwitchRecovery,
					expected: true,
					recovery: "restart_sign_in"
				});
			} else {
				logError("auth.handleAuthentication: callback failed", err, {
					errCode: err?.error,
					errDesc: err?.error_description,
					orgSwitchRecovery
				});
			}

			// organization_id from the recovered appState deliberately wins over caller-supplied
			// values — preserving the requested org through recovery is the point of this branch.
			const withOrgSwitchContext = (authorizationParams) => ({
				...(orgSwitchAppState ? { appState: orgSwitchAppState } : {}),
				authorizationParams: {
					...authorizationParams,
					...(orgSwitchAppState ? { organization_id: orgSwitchAppState.organizationId } : {})
				}
			});
			// recoveryMode tags the log when loginWithRedirect itself rejects (rare: blocked
			// storage, SDK init failure). Without it, silent and interactive failures look
			// identical in Datadog and you can't tell which branch tripped.
			const recover = async (recoveryMode, authorizationParams) => {
				try {
					// LSP-17342: latch this redirect too. It runs in the /callback document and
					// is covered by the callback-route rule today, but routing it through the
					// shared funnel keeps the coverage from depending on that assumption.
					// isExplicitUserIntent: nobody asked for this redirect — it is this method's own
					// reaction to a failed code/state exchange. It is exactly the redirect break-it
					// round 5 caught racing a bfcache-restore recovery and landing a live session on
					// the Auth0 login screen, so it stays suppressed while a recovery owns the page.
					await this._redirectToAuth0(client, withOrgSwitchContext(authorizationParams), `handleAuthentication:${recoveryMode}`, {
						isExplicitUserIntent: false
					});
				} catch (retryErr) {
					logError("auth.handleAuthentication: recovery loginWithRedirect failed", retryErr, {
						recoveryMode,
						orgSwitchRecovery
					});
					if (orgSwitchAppState) {
						const recoveryError = new Error(retryErr?.message || "Auth0 recovery redirect failed");
						recoveryError.cause = retryErr;
						recoveryError.appState = orgSwitchAppState;
						throw recoveryError;
					}
					throw retryErr;
				}
			};
			const silent = () => recover("silent", { prompt: "none" });
			const interactive = (extraParams = {}) => recover("interactive", { prompt: "login", ...extraParams });

			// "User is blocked" can come back as either `unauthorized` or `access_denied`
			// from Auth0 depending on the connection. Gate on the description AND require
			// one of those two codes so unrelated errors that happen to mention "blocked"
			// (popup blocked, IP blocked, blocked resource) don't accidentally trip the
			// blocked-user UI and short-circuit recovery.
			if (errDesc.includes("blocked") && (errCode === "unauthorized" || errCode === "access_denied")) {
				this.blocked = true;
				finishWithError(err);
				throw err;
			}

			// A metadata-size access_denied is PERMANENT: the user's Auth0 metadata exceeds the 32KB
			// limit server-side, so every recovery redirect (interactive/silent) returns the SAME error
			// and spins /callback forever (LSP-16960: one user produced ~11,256 reloads in a single day,
			// tab frozen, never able to sign in). Treat it as terminal — reuse the blocked-user surface
			// (BlockedUser.vue: generic "trouble accessing your account, contact help@liveswitch.com")
			// instead of recovering. login() clears `blocked` on a fresh attempt, so a user whose
			// metadata is trimmed server-side recovers without stigma. (The metadata bloat itself is a
			// separate backend follow-up — see LSP-16960.)
			if (errCode === "access_denied" && errDesc.includes("metadata") && errDesc.includes("exceeded")) {
				this.blocked = true;
				finishWithError(err);
				throw err;
			}

			try {
				// SSO-required errors must be handled before the generic access_denied/default cases,
				// otherwise the SSO-specific params (error_code/error_description) get dropped and
				// users see a generic login prompt instead of the SSO-specific guidance.
				// Inside this try/finally so a `loginWithRedirect` throw still settles `callbackDone`
				// instead of leaking a pending promise that hangs `waitForCallbackToFinish`.
				if (errCode === "use_sso" || errDesc.includes("use_sso")) {
					await interactive({
						error_code: "use_sso",
						error_description: err?.error_description || err?.message || "Please use your Google account to login."
					});
					return null;
				}

				switch (errCode) {
					case "missing_transaction":
					case "invalid_state":
					case "state_mismatch":
						// LSP-17341: Auth0 cannot replay a consumed browser-history transaction.
						// Restart visibly so the user can sign in again instead of reaching its error page.
						await interactive();
						return null;

					case "login_required":
					case "interaction_required":
					case "access_denied":
					case "unauthorized":
						await interactive();
						return null;

					case "consent_required":
						await interactive({ prompt: "consent" });
						return null;

					case "mfa_required":
						await interactive();
						return null;

					case "timeout":
					case "server_error":
					case "network_error":
						await new Promise((r) => setTimeout(r, 500));
						await silent();
						return null;

					case "origin_mismatch":
						logError("auth.handleAuthentication: origin mismatch — check Allowed Callback URLs in Auth0", err);
						finishWithError(err);
						throw err;

					case "invalid_grant":
					case "invalid_token":
						clearAuth0LocalCache();
						await interactive();
						return null;

					default:
						// Capture unknown errCodes in Datadog so we can spot new Auth0 conditions
						// without waiting for a user report.
						logError("auth.handleAuthentication: unhandled errCode, defaulting to interactive login", err, {
							errCode,
							errDesc
						});
						if (errDesc.includes("mfa")) {
							await interactive();
							return null;
						}
						if (errDesc.includes("consent")) {
							await interactive({ prompt: "consent" });
							return null;
						}
						await interactive();
						return null;
				}
			} finally {
				// Recovery branches above either redirect or no-op; either way the in-flight
				// callback is settled and pending getAccessToken waiters can proceed.
				if (resolveCallback) {
					finishWithSuccess();
				}
			}
		}
	}
}

const auth = new AuthService();

const authPlugin = {
	install(appOrVue) {
		const target = appOrVue?.config?.globalProperties || appOrVue?.prototype;
		if (!target) {
			console.error("Auth plugin install failed: unsupported Vue instance.");
			return;
		}
		target.$auth = auth;
		auth.init();
	},
	getInstance() {
		auth.init();
		return auth;
	}
};

export default authPlugin;
export { auth };
